import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import DesignServicesRoundedIcon from "@mui/icons-material/DesignServicesRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import QrCodeScannerRoundedIcon from "@mui/icons-material/QrCodeScannerRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import HandymanRoundedIcon from "@mui/icons-material/HandymanRounded";
import AddCardRoundedIcon from "@mui/icons-material/AddCardRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { productsApi } from "../../api/products";
import { activityApi } from "../../api/activity";
import { promotionsApi } from "../../api/promotions";
import { recipesApi } from "../../api/recipes";
import { projectsApi } from "../../api/projects";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import { isProjectCapabilityEnabled } from "../../utils/projectCapabilities";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import ResponsiveListControls from "../../components/dashboard/ResponsiveListControls";
import ResponsiveOperationalTable from "../../components/dashboard/ResponsiveOperationalTable";
import MobileListPagination from "../../components/dashboard/MobileListPagination";
import SearchPicker from "../../components/dashboard/SearchPicker";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import { getProductImageUrls } from "../../utils/productMedia";
import {
  formatImageSize,
  optimizeProductImages,
} from "../../utils/productImageUpload";
import {
  PRODUCT_IMAGE_MAX_SOURCE_BYTES,
  describeImageUploadError,
  uploadProgressPercent,
} from "../../utils/imageUpload";
import ItemOperationalFields, { capabilitiesForKind } from "../../components/dashboard/items/ItemOperationalFields";
import ProductVariantsEditor from "../../components/dashboard/items/ProductVariantsEditor";
import BarcodeScannerDialog from "../../components/dashboard/items/BarcodeScannerDialog";
import ProductImportWizard from "../../components/dashboard/items/import/ProductImportWizard";
import {
  engageDashboardTutorialTarget,
  loadDashboardTutorialState,
  pauseDashboardTutorial,
  reportActiveDashboardTutorialMetric,
  resumeDashboardTutorial,
  startDashboardTutorial,
} from "../../utils/dashboardTutorial";
import GastronomicItemWizard from "../../components/dashboard/items/GastronomicItemWizard";
import GastronomicMenuOrderDialog from "../../components/dashboard/items/GastronomicMenuOrderDialog";
import ItemImagesEditor from "../../components/dashboard/items/ItemImagesEditor";
import GastronomicAdvancedEditor from "../../components/dashboard/items/GastronomicAdvancedEditor";
import GastronomicPromotionsEditor, { CURRENT_PRODUCT } from "../../components/dashboard/items/GastronomicPromotionsEditor";
import GastronomicSectionPositionEditor from "../../components/dashboard/items/GastronomicSectionPositionEditor";
import RecipesWorkspace from "../../components/dashboard/gastronomy/RecipesWorkspace";
import QuantityDiscountsEditor, {
  normalizeQuantityDiscountRows,
  quantityDiscountEligibility,
  validateQuantityDiscountRows,
} from "../../components/dashboard/items/QuantityDiscountsEditor";
import { hasProjectPermission, normalizeProjectAccess } from "../../utils/projectAccess";
import {
  isTrialProject,
  TRIAL_PRODUCTS_EVENT,
} from "../../utils/trialOnboarding";
import { isValidCalendlyUrl } from "../../utils/calendly";
import { downloadBarcodeLabelsPdf } from "../../utils/barcodeLabelsPdf";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };
const MAX_IMAGES = 8;

const REMOVE_IMAGE_BUTTON_SX = {
  position: "absolute",
  top: -8,
  right: -8,
  width: 44,
  height: 44,
  p: 0,
  bgcolor: "transparent",
  zIndex: 1,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: 24,
    height: 24,
    borderRadius: "50%",
    bgcolor: "var(--wp-panel-surface-elevated, white)",
    boxShadow: 1,
  },
  "&:hover": { bgcolor: "transparent" },
  "&:hover::before": { bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)" },
  "&.Mui-focusVisible": {
    outline: `2px solid ${BRAND.orange}`,
    outlineOffset: 1,
  },
};

const emptyForm = {
  itemType: "product",
  erpItemKind: "product",
  name: "",
  description: "",
  category: "General",
  subcategory: "",
  gastronomicEnabled: false,
  gastronomicAvailable: true,
  gastronomicShortDescription: "",
  gastronomicDietaryNotes: "",
  gastronomicSection: "",
  gastronomicSectionOrder: "0",
  gastronomicSortOrder: "0",
  gastronomicFeatured: false,
  code: "",
  unit: "",
  priceType: "fixed",
  priceCurrency: "ARS",
  price: "0",
  compareAtPrice: "",
  quantityPriceTiers: [],
  stock: "0",
  durationMinutes: "",
  modality: "",
  requiresBooking: false,
  requiresShipping: true,
  bookingProvider: "none",
  calendlyUrl: "",
  calendlyDisplayMode: "popup",
  bookingConfirmationMode: "manual",
  bookingPaymentMode: "none",
  bookingDepositType: "fixed",
  bookingDepositValue: "0",
  capacity: "1",
  bufferBeforeMinutes: "0",
  bufferAfterMinutes: "0",
  serviceLocation: "",
  meetingUrl: "",
  bookingInstructions: "",
  cancellationWindowHours: "0",
  tags: "",
  attributes: [],
  hasVariants: false,
  variantAttributes: [],
  variants: [],
  active: true,
  membershipEligible: false,
  membershipPeriodValue: "1",
  membershipPeriodUnit: "months",
  membershipReferencePrice: "",
  membershipUsageEnabled: false,
  membershipIncludedUses: "",
  membershipUsageLabel: "usos",
  ...capabilitiesForKind("product"),
  lastCost: "0",
  averageCost: "0",
  standardCost: "0",
  costingMethod: "last_cost",
  costCurrency: "ARS",
  allowNegativeStock: false,
  minimumStock: "0",
  reorderPoint: "0",
  inventoryLocation: "",
  salesAccountCode: "SALES",
  purchaseAccountCode: "PURCHASES",
  inventoryAccountCode: "INVENTORY",
  costAccountCode: "COST_OF_GOODS",
  expenseAccountCode: "OTHER_EXPENSE",
  taxProfileCode: "",
  taxRate: "0",
  taxIncludedInPrice: false,
};

function normalizeItemType(product = {}) {
  const value = String(
    product.itemType || product.type || product.tipo || "product",
  )
    .trim()
    .toLowerCase();
  return ["service", "servicio"].includes(value) ? "service" : "product";
}

const ITEM_KIND_LABELS = {
  product: "Producto para vender",
  service: "Servicio para vender",
  supply: "Insumo o consumible",
  expense: "Gasto habitual",
  asset: "Activo o equipamiento",
  charge: "Cargo o concepto",
};

const ITEM_KIND_VISUALS = {
  product: { label: "Producto", detail: "Lo vendés y puede tener stock.", Icon: ShoppingBagRoundedIcon },
  service: { label: "Servicio", detail: "Lo vendés por turno, sesión o trabajo.", Icon: RoomServiceRoundedIcon },
  supply: { label: "Insumo", detail: "Lo comprás o consumís para trabajar.", Icon: Inventory2RoundedIcon },
  expense: { label: "Gasto", detail: "Un gasto habitual que querés registrar.", Icon: ReceiptLongRoundedIcon },
  asset: { label: "Equipamiento", detail: "Una herramienta o activo del negocio.", Icon: HandymanRoundedIcon },
  charge: { label: "Cargo", detail: "Un concepto extra que podés cobrar.", Icon: AddCardRoundedIcon },
};

function normalizeItemKind(product = {}) {
  const value = String(
    product.erpItemKind || product.itemKind || product.itemClass || normalizeItemType(product),
  )
    .trim()
    .toLowerCase();
  return ITEM_KIND_LABELS[value] ? value : normalizeItemType(product);
}

function productCapabilities(product = {}) {
  const itemKind = normalizeItemKind(product);
  const defaults = capabilitiesForKind(itemKind);
  const supplied = {
    ...(product.capabilities || {}),
    ...(product.operational || {}),
  };

  return {
    ...defaults,
    ...supplied,
    reservable: Boolean(
      supplied.reservable ??
        product.requiresBooking ??
        product.requiereReserva ??
        defaults.reservable,
    ),
    shippable: Boolean(
      supplied.shippable ??
        product.requiresShipping ??
        product.requiereEnvio ??
        defaults.shippable,
    ),
  };
}

function supportsInventoryForKind(kind) {
  return kind === "product" || kind === "supply";
}

function supportsVariantsForKind(kind) {
  return supportsInventoryForKind(kind);
}

function isReservableService(form = {}) {
  return (
    form.erpItemKind === "service" &&
    Boolean(form.reservable || form.requiresBooking)
  );
}

function normalizePriceType(product = {}) {
  const value = String(product.priceType || product.precioTipo || "fixed")
    .trim()
    .toLowerCase();
  if (["from", "desde"].includes(value)) return "from";
  if (["quote", "consultar"].includes(value)) return "quote";
  return "fixed";
}

function formatCatalogMoneyValue(value, currency = "ARS", { fixedDecimals = false } = {}) {
  if (value === "" || value === null || value === undefined) return "";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value || "");
  if (String(currency || "ARS").toUpperCase() !== "ARS") {
    return amount.toLocaleString("es-AR", {
      minimumFractionDigits: fixedDecimals ? 2 : 0,
      maximumFractionDigits: 2,
    });
  }
  return amount.toLocaleString("es-AR", {
    minimumFractionDigits: fixedDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

function normalizeCatalogMoneyDraft(rawValue, currency = "ARS") {
  const raw = String(rawValue ?? "").replace(/\s/g, "").replace(/[^0-9.,]/g, "");
  if (!raw) return { display: "", value: "" };

  const normalizedCurrency = String(currency || "ARS").toUpperCase();
  if (normalizedCurrency !== "ARS") {
    const lastSeparator = Math.max(raw.lastIndexOf(","), raw.lastIndexOf("."));
    const hasDecimal = lastSeparator >= 0 && raw.length - lastSeparator - 1 <= 2;
    const integerDigits = (hasDecimal ? raw.slice(0, lastSeparator) : raw).replace(/\D/g, "") || "0";
    const decimals = hasDecimal ? raw.slice(lastSeparator + 1).replace(/\D/g, "").slice(0, 2) : "";
    const display = `${Number(integerDigits).toLocaleString("es-AR")}${hasDecimal ? `,${decimals}` : ""}`;
    return { display, value: `${Number(integerDigits)}${decimals ? `.${decimals}` : ""}` };
  }

  const commaIndex = raw.lastIndexOf(",");
  const dotIndex = raw.lastIndexOf(".");
  const dotLooksDecimal = commaIndex < 0 && dotIndex >= 0 && raw.length - dotIndex - 1 <= 2;
  const decimalIndex = commaIndex >= 0 ? commaIndex : dotLooksDecimal ? dotIndex : -1;
  const integerSource = decimalIndex >= 0 ? raw.slice(0, decimalIndex) : raw;
  const integerDigits = integerSource.replace(/\D/g, "") || "0";
  const decimals = decimalIndex >= 0 ? raw.slice(decimalIndex + 1).replace(/\D/g, "").slice(0, 2) : "";
  const hasDecimalMarker = decimalIndex >= 0;
  const groupedInteger = Number(integerDigits).toLocaleString("es-AR");
  return {
    display: `${groupedInteger}${hasDecimalMarker ? `,${decimals}` : ""}`,
    value: `${Number(integerDigits)}${decimals ? `.${decimals}` : ""}`,
  };
}

function attributesToRows(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return [];
  return Object.entries(value)
    .map(([label, detail]) => ({
      label: String(label),
      value: String(detail ?? ""),
    }))
    .filter((row) => row.label || row.value);
}

function rowsToAttributes(rows = []) {
  return rows.reduce((result, row) => {
    const label = String(row?.label || "").trim();
    const value = String(row?.value || "").trim();
    if (label && value) result[label] = value;
    return result;
  }, {});
}

function tagsToText(product = {}) {
  const tags = product.tags || product.etiquetas || [];
  return (Array.isArray(tags) ? tags : [])
    .map((tag) => (typeof tag === "string" ? tag : tag?.title))
    .filter(Boolean)
    .join(", ");
}

function variantAttributesToForm(product = {}) {
  const source = product.variantAttributes || product.variantOptions || [];
  return (Array.isArray(source) ? source : []).map((axis, index) => ({
    key: String(axis?.key || axis?.id || `opcion_${index + 1}`),
    label: String(axis?.label || axis?.name || `Opción ${index + 1}`),
    values: Array.isArray(axis?.values || axis?.options)
      ? (axis.values || axis.options).map(String).filter(Boolean)
      : [],
    displayType: axis?.displayType || axis?.type || "button",
  }));
}

function variantsToForm(product = {}) {
  const source = product.variants || product.variations || [];
  return (Array.isArray(source) ? source : []).map((variant) => ({
    id: String(variant?.id || variant?._id || ""),
    attributes: variant?.attributes || variant?.combination || {},
    sku: variant?.sku || variant?.codigo || "",
    barcode: variant?.barcode || "",
    price:
      variant?.price === null || variant?.price === undefined
        ? ""
        : String(variant.price),
    stock: String(variant?.stock ?? 0),
    imageRef: variant?.imagePath || "",
    active: variant?.active !== false,
  }));
}

function quantityPriceTiersToForm(product = {}) {
  const source =
    product.quantityPriceTiers ||
    product.quantityDiscounts ||
    product.descuentosPorCantidad ||
    [];
  return (Array.isArray(source) ? source : []).map((tier, index) => ({
    key: `quantity-tier-${productId(product) || "item"}-${index}`,
    minQuantity: String(tier?.minQuantity ?? tier?.quantity ?? ""),
    unitPrice: String(tier?.unitPrice ?? tier?.price ?? ""),
  }));
}

function validateVariantForm(form = {}) {
  if (!form.hasVariants || !supportsVariantsForKind(form.erpItemKind)) return "";
  const axes = Array.isArray(form.variantAttributes) ? form.variantAttributes : [];
  const variants = Array.isArray(form.variants) ? form.variants : [];
  if (!axes.length) return "Agregá al menos una versión o característica.";
  for (const axis of axes) {
    if (!String(axis.label || "").trim()) return "Completá el nombre de cada característica.";
    if (!Array.isArray(axis.values) || !axis.values.length) {
      return `Agregá opciones para ${axis.label || "cada característica"}.`;
    }
  }
  if (!variants.length) return "Agregá al menos una variante antes de guardar.";
  if (variants.length > 250) return "El producto no puede superar 250 variantes.";

  const skuSet = new Set();
  const combinationSet = new Set();
  for (const variant of variants) {
    const combinationParts = [];
    for (const axis of axes) {
      const value = String(variant.attributes?.[axis.key] || "").trim();
      if (!value) return `Falta ${axis.label || "una opción"} en una variante.`;
      combinationParts.push(`${axis.key}:${value.toLocaleLowerCase("es")}`);
    }
    const combination = combinationParts.join("|");
    if (combinationSet.has(combination)) return "Hay combinaciones de variantes repetidas.";
    combinationSet.add(combination);

    const sku = String(variant.sku || "").trim().toLocaleLowerCase("es");
    if (sku && skuSet.has(sku)) return `El SKU ${variant.sku} está repetido.`;
    if (sku) skuSet.add(sku);
  }
  return "";
}

function variantImageOptions(existingImages = [], previewUrls = []) {
  return [
    ...existingImages.map((image) => ({ ref: image.identifier, url: image.url })),
    ...previewUrls.map((url) => ({ ref: url, url })),
  ];
}

function normalizeProductPayload(
  form,
  imageOptions = [],
  { membershipsEnabled = false } = {},
) {
  const isService = form.erpItemKind === "service";
  const reservable = isReservableService(form);
  const shippable =
    form.erpItemKind === "product" &&
    Boolean(form.shippable ?? form.requiresShipping);
  const hasVariants =
    supportsVariantsForKind(form.erpItemKind) && Boolean(form.hasVariants);
  const variants = hasVariants
    ? (Array.isArray(form.variants) ? form.variants : []).map((variant) => ({
        ...(variant.id ? { id: variant.id } : {}),
        attributes: variant.attributes || {},
        sku: String(variant.sku || "").trim(),
        barcode: String(variant.barcode || "").trim(),
        price:
          variant.price === "" || variant.price === null || variant.price === undefined
            ? null
            : Number(variant.price),
        imageIndex: imageOptions.findIndex((option) => option.ref === variant.imageRef),
        active: variant.active !== false,
      }))
    : [];
  const quantityPricingAllowed = quantityDiscountEligibility(form).allowed;
  return {
    itemType: isService ? "service" : "product",
    erpItemKind: form.erpItemKind,
    itemKind: form.erpItemKind,
    name: form.name,
    description: form.description,
    category: form.category || "General",
    subcategory: form.subcategory || "",
    code: form.code,
    unit: form.unit,
    priceType: form.priceType,
    ...(["ARS", "USD"].includes(form.priceCurrency) ? { priceCurrency: form.priceCurrency } : {}),
    price: Number(form.price || 0),
    compareAtPrice: form.compareAtPrice === "" || form.compareAtPrice === null ? null : Number(form.compareAtPrice),
    quantityPriceTiers: quantityPricingAllowed
      ? normalizeQuantityDiscountRows(form.quantityPriceTiers)
      : [],
    hasVariants,
    variantAttributes: hasVariants
      ? (form.variantAttributes || []).map((axis) => ({
          key: axis.key,
          label: axis.label,
          values: Array.isArray(axis.values) ? axis.values : [],
          displayType: axis.displayType || "button",
        }))
      : [],
    variants,
    operational: {
      sellable: Boolean(form.sellable),
      purchasable: Boolean(form.purchasable),
      reservable,
      shippable,
      publicVisible: Boolean(form.publicVisible),
      expenseEnabled: Boolean(form.expenseEnabled),
      internalConsumptionEnabled: Boolean(form.internalConsumptionEnabled),
      stockTracked: Boolean(form.stockTracked),
      ...(membershipsEnabled ? { membershipEligible: Boolean(form.membershipEligible) } : {}),
    },
    inventory: {
      allowNegativeStock: Boolean(form.allowNegativeStock),
      minimumStock: Number(form.minimumStock || 0),
      reorderPoint: Number(form.reorderPoint || 0),
      location: String(form.inventoryLocation || "").trim(),
    },
    ...(membershipsEnabled
      ? {
          membershipPlan: form.membershipEligible
            ? {
                periodValue: Number(form.membershipPeriodValue || 1),
                periodUnit: form.membershipPeriodUnit || "months",
                referencePrice: form.membershipReferencePrice === "" ? Number(form.price || 0) : Number(form.membershipReferencePrice || 0),
                usageEnabled: Boolean(form.membershipUsageEnabled),
                includedUses: form.membershipUsageEnabled ? Number(form.membershipIncludedUses || 0) : null,
                usageLabel: String(form.membershipUsageLabel || "usos").trim() || "usos",
              }
            : {
                usageEnabled: false,
                includedUses: null,
                usageLabel: String(form.membershipUsageLabel || "usos").trim() || "usos",
              },
        }
      : {}),
    costing: {
      lastCost: Number(form.lastCost || 0),
      averageCost: Number(form.averageCost || 0),
      standardCost: Number(form.standardCost || 0),
      costingMethod: form.costingMethod || "last_cost",
      currency: form.costCurrency || "ARS",
    },
    accounting: {
      salesAccountCode: form.salesAccountCode || "SALES",
      purchaseAccountCode: form.purchaseAccountCode || "PURCHASES",
      inventoryAccountCode: form.inventoryAccountCode || "INVENTORY",
      costAccountCode: form.costAccountCode || "COST_OF_GOODS",
      expenseAccountCode: form.expenseAccountCode || "OTHER_EXPENSE",
    },
    tax: {
      profileCode: form.taxProfileCode || "",
      rate: Number(form.taxRate || 0),
      includedInPrice: Boolean(form.taxIncludedInPrice),
    },
    durationMinutes: isService ? Number(form.durationMinutes || 0) : 0,
    modality: isService ? form.modality : "",
    requiresBooking: reservable,
    requiresShipping: shippable,
    bookingProvider: reservable
      ? (form.bookingProvider === "calendly" ? "calendly" : "webprofit")
      : "none",
    calendlyUrl:
      reservable && form.bookingProvider === "calendly"
        ? String(form.calendlyUrl || "").trim()
        : "",
    calendlyDisplayMode:
      reservable && form.bookingProvider === "calendly" && form.calendlyDisplayMode === "inline"
        ? "inline"
        : "popup",
    bookingConfirmationMode:
      reservable && form.bookingProvider !== "calendly" ? form.bookingConfirmationMode : "manual",
    bookingPaymentMode:
      reservable && form.bookingProvider !== "calendly" ? form.bookingPaymentMode : "none",
    bookingDepositType:
      reservable && form.bookingProvider !== "calendly" ? form.bookingDepositType : "fixed",
    bookingDepositValue:
      reservable && form.bookingProvider !== "calendly" ? Number(form.bookingDepositValue || 0) : 0,
    capacity:
      reservable && form.bookingProvider !== "calendly" ? Math.max(1, Number(form.capacity || 1)) : 1,
    bufferBeforeMinutes:
      reservable && form.bookingProvider !== "calendly" ? Number(form.bufferBeforeMinutes || 0) : 0,
    bufferAfterMinutes:
      reservable && form.bookingProvider !== "calendly" ? Number(form.bufferAfterMinutes || 0) : 0,
    serviceLocation: isService ? form.serviceLocation : "",
    meetingUrl:
      reservable && form.bookingProvider !== "calendly" ? form.meetingUrl : "",
    bookingInstructions: reservable ? form.bookingInstructions : "",
    cancellationWindowHours:
      reservable && form.bookingProvider !== "calendly" ? Number(form.cancellationWindowHours || 0) : 0,
    tags: String(form.tags || "")
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean),
    attributes: rowsToAttributes(form.attributes),
    active: Boolean(form.active),
  };
}

function promotionToDraft(promotion = {}) {
  return {
    key: promotion.id || `promo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    id: promotion.id || "",
    name: promotion.name || "",
    active: promotion.active !== false,
    type: promotion.type === "multi_product_combo" ? "multi_product_combo" : "same_product_pack",
    fixedTotal: String(promotion.fixedTotal ?? ""),
    revision: promotion.revision || 1,
    reviewRequired: promotion.reviewRequired === true,
    reviewReasons: promotion.reviewReasons || [],
    lines: (promotion.lines || []).map((line) => ({
      productId: String(line.productId || ""),
      quantity: String(line.quantity || 1),
      variantMode: line.variantMode === "specific" ? "specific" : "any",
      variantIds: (line.variantIds || []).map(String),
    })),
  };
}

function validatePromotionDrafts(drafts = [], currentProductId = "") {
  for (const draft of drafts.filter((item) => !item._delete)) {
    if (!String(draft.name || "").trim()) return "Cada promoción necesita un nombre.";
    if (!(Number(draft.fixedTotal) > 0)) return "Cada promoción necesita un precio final mayor que cero.";
    const lines = Array.isArray(draft.lines) ? draft.lines : [];
    if (draft.type === "same_product_pack" && (lines.length !== 1 || Number(lines[0]?.quantity) < 2)) return "Un pack necesita al menos 2 unidades del mismo producto.";
    if (draft.type === "multi_product_combo" && lines.length < 2) return "Un combo necesita al menos dos productos o líneas.";
    for (const line of lines) {
      const resolvedId = line.productId === CURRENT_PRODUCT ? currentProductId || CURRENT_PRODUCT : line.productId;
      if (!resolvedId) return "Elegí todos los productos que forman la promoción.";
      const quantity = Number(line.quantity);
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) return "Las cantidades de una promoción deben ser números enteros entre 1 y 99.";
      if (line.variantMode === "specific" && !(line.variantIds || []).length) return "Elegí las variantes habilitadas o usá ‘Cualquier opción’.";
    }
  }
  return "";
}

function promotionPayloadFromDraft(draft, currentProductId) {
  return {
    name: String(draft.name || "").trim(),
    active: draft.active !== false,
    type: draft.type === "multi_product_combo" ? "multi_product_combo" : "same_product_pack",
    fixedTotal: Number(draft.fixedTotal),
    lines: (draft.lines || []).map((line) => ({
      productId: line.productId === CURRENT_PRODUCT ? currentProductId : line.productId,
      quantity: Number(line.quantity),
      variantMode: line.variantMode === "specific" ? "specific" : "any",
      variantIds: line.variantMode === "specific" ? (line.variantIds || []) : [],
    })),
  };
}

function priceLabel(product = {}, currency = "ARS") {
  const price = Number(product.price ?? product.precio ?? 0);
  const type = normalizePriceType(product);
  if (type === "quote") return "A consultar";
  const candidate = product.priceCurrency || product.precioMoneda || currency;
  const code = /^[A-Z]{3}$/.test(String(candidate || "").trim().toUpperCase())
    ? String(candidate).trim().toUpperCase()
    : "ARS";
  let formatted;
  try {
    formatted = new Intl.NumberFormat("es-AR", { style: "currency", currency: code }).format(price);
  } catch (_error) {
    formatted = `${code} ${price.toLocaleString("es-AR")}`;
  }
  if (code === "USD") formatted = `USD ${price.toLocaleString("es-AR", { maximumFractionDigits: 2 })}`;
  return type === "from" ? `Desde ${formatted}` : formatted;
}

function durationLabel(minutes) {
  const value = Number(minutes || 0);
  if (!value) return "Duración no indicada";
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function productId(product) {
  const source = product || {};
  return (
    source.id ||
    source._id ||
    source.code ||
    source.codigo ||
    source.name ||
    source.nombre ||
    ""
  );
}

function productName(product = {}) {
  return product.name || product.nombre || "Sin nombre";
}

function productDescription(product = {}) {
  return product.description || product.descripcion || "";
}

function productCategory(product = {}) {
  return product.category || product.categoria || "General";
}

function productSubcategory(product = {}) {
  return product.subcategory || product.subcategoria || "";
}

function productCode(product = {}) {
  return product.code || product.codigo || "";
}

function productIsActive(product = {}) {
  return Boolean(product.active ?? product.activo ?? true);
}

function productStock(product = {}) {
  return Number(product.stock ?? 0);
}

function MobileCatalogItemCard({
  product,
  catalogCurrency,
  canUpdateItems,
  canDeleteItems,
  canManageStock,
  recipesEnabled,
  recipeSummary,
  onEdit,
  onStock,
  onRecipe,
  onDelete,
}) {
  const itemKind = normalizeItemKind(product);
  const isService = itemKind === "service";
  const capabilities = productCapabilities(product);
  const isActive = productIsActive(product);
  const imageUrl = getProductImageUrls(product)[0];
  const stock = productStock(product);
  const displayName = productName(product);
  const code = productCode(product);
  const unit = product.unit || product.unidad || "";
  const category = productCategory(product);
  const subcategory = productSubcategory(product);
  const lowStock = !isService && capabilities.stockTracked && stock <= Number(product.inventory?.reorderPoint ?? 5);

  return (
    <Paper
      variant="outlined"
      sx={{
        mx: .8,
        my: .75,
        p: 1.15,
        borderRadius: 2.5,
        borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
        bgcolor: "var(--wp-panel-surface, #fff)",
        boxShadow: "0 5px 16px rgba(25,36,47,.04)",
      }}
    >
      <Stack spacing={1.05}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Avatar
            variant="rounded"
            src={imageUrl || undefined}
            alt={displayName}
            sx={{
              width: 48,
              height: 48,
              flex: "0 0 auto",
              borderRadius: 2,
              bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.06)",
              border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)",
              color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
            }}
          >
            {isService ? <DesignServicesRoundedIcon fontSize="small" /> : <ImageRoundedIcon fontSize="small" />}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                color: BRAND.dark,
                fontWeight: 1000,
                fontSize: ".92rem",
                lineHeight: 1.18,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {displayName}
            </Typography>
            <Stack direction="row" spacing={.45} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: .55 }}>
              <Chip size="small" label={ITEM_KIND_LABELS[itemKind] || itemKind} variant="outlined" sx={{ height: 21, fontWeight: 850, "& .MuiChip-label": { px: .75, fontSize: ".64rem" } }} />
              <Chip
                size="small"
                label={isActive ? "Activo" : "Oculto"}
                sx={{
                  height: 21,
                  fontWeight: 850,
                  color: isActive ? "#237A45" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.58)",
                  bgcolor: isActive ? "rgba(46,160,83,0.1)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.07)",
                  "& .MuiChip-label": { px: .75, fontSize: ".64rem" },
                }}
              />
              {product.hasVariants || product.variants?.length ? (
                <Chip size="small" label={`${(product.variants || product.variations || []).length} var.`} sx={{ height: 21, fontSize: ".64rem", fontWeight: 900 }} />
              ) : null}
            </Stack>
          </Box>
        </Stack>

        <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: .7 }}>
          <Box sx={{ p: .8, borderRadius: 2, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)" }}>
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontWeight: 800, fontSize: ".62rem" }}>PRECIO</Typography>
            <Typography sx={{ mt: .1, color: BRAND.dark, fontWeight: 1000, fontSize: ".88rem", lineHeight: 1.15, overflowWrap: "anywhere" }}>
              {priceLabel(product, catalogCurrency)}{unit ? ` / ${unit}` : ""}
            </Typography>
          </Box>
          <Box sx={{ p: .8, borderRadius: 2, bgcolor: lowStock ? "rgba(243,90,58,.075)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.025)" }}>
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontWeight: 800, fontSize: ".62rem" }}>
              {isService ? "DURACIÓN" : "STOCK"}
            </Typography>
            <Typography sx={{ mt: .1, color: lowStock ? "#C4452D" : BRAND.dark, fontWeight: 1000, fontSize: ".88rem", lineHeight: 1.15 }}>
              {isService
                ? `${durationLabel(product.durationMinutes || product.duracionMinutos)} · ${product.modality || product.modalidad || "Sin modalidad"}`
                : capabilities.stockTracked ? stock : "Sin control"}
            </Typography>
          </Box>
        </Box>

        {(code || category || subcategory) ? (
          <Stack direction="row" spacing={.75} useFlexGap flexWrap="wrap" alignItems="center">
            {code ? <Typography variant="caption" sx={{ fontFamily: "monospace", color: BRAND.dark, fontWeight: 800 }}>{code}</Typography> : null}
            {category ? <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 750 }}>{category}{subcategory ? ` · ${subcategory}` : ""}</Typography> : null}
          </Stack>
        ) : null}

        <Stack
          direction="row"
          spacing={.65}
          useFlexGap
          flexWrap="wrap"
          sx={{ pt: .9, borderTop: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", "& .MuiButton-root, & .MuiIconButton-root": { minHeight: 44 } }}
        >
          {canUpdateItems ? (
            <Button
              size="small"
              variant="contained"
              startIcon={<EditRoundedIcon />}
              onClick={() => onEdit(product)}
              sx={{ flex: "1 1 128px", borderRadius: 2, textTransform: "none", fontWeight: 1000, color: "#17181C", bgcolor: BRAND.orange, boxShadow: "none", "&:hover": { bgcolor: "#FF714F", boxShadow: "none" } }}
            >
              Editar
            </Button>
          ) : null}
          {!isService && capabilities.stockTracked && canManageStock ? (
            <Button
              size="small"
              variant={stock === 0 ? "contained" : "outlined"}
              startIcon={<Inventory2RoundedIcon />}
              onClick={() => onStock(product)}
              sx={{ flex: "1 1 128px", borderRadius: 2, textTransform: "none", fontWeight: 950, ...(stock === 0 ? { bgcolor: BRAND.orange, color: "#17181C", "&:hover": { bgcolor: "#FF714F" } } : {}) }}
            >
              {stock === 0 ? "Cargar stock" : lowStock ? "Reponer" : "Stock"}
            </Button>
          ) : null}
          {recipesEnabled && product.gastronomicMenu?.enabled === true ? (
            <Button size="small" variant="outlined" startIcon={<RestaurantMenuRoundedIcon />} onClick={() => onRecipe(product)} sx={{ flex: "1 1 112px", borderRadius: 2, textTransform: "none", fontWeight: 900, color: recipeSummary ? "success.main" : BRAND.dark }}>
              Receta
            </Button>
          ) : null}
          {canDeleteItems ? (
            <Tooltip title="Eliminar ítem">
              <IconButton onClick={() => onDelete(product)} aria-label={`Eliminar ${displayName}`} sx={{ minWidth: 44, border: "1px solid rgba(243,90,58,.24)", borderRadius: 2, color: BRAND.orange }}>
                <DeleteRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}

function normalizeSearchValue(value) {
  return String(value ?? "")
    .trim()
    .toLocaleLowerCase("es");
}

function uniqueClassificationValues(values = []) {
  const byKey = new Map();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const label = String(value || "").trim();
    const key = normalizeSearchValue(label);
    if (key && !byKey.has(key)) byKey.set(key, label);
  });
  return [...byKey.values()].sort((a, b) =>
    a.localeCompare(b, "es", { sensitivity: "base" }),
  );
}

function sameClassification(left, right) {
  const leftKey = normalizeSearchValue(left);
  const rightKey = normalizeSearchValue(right);
  return Boolean(leftKey && rightKey && leftKey === rightKey);
}

function validateBasicItemFields(form = {}, { requireCategory = false } = {}) {
  const fields = {};
  const name = String(form.name || "").trim();
  const category = String(form.category || "").trim();
  const price = Number(form.price);
  const compareAtPrice = Number(form.compareAtPrice);

  if (!name) fields.name = "Ingresá un nombre para el ítem.";
  if (requireCategory && !category) fields.category = "Elegí una sección para este ítem.";
  if (form.price !== "" && (!Number.isFinite(price) || price < 0)) {
    fields.price = "Ingresá un precio válido, igual o mayor a 0.";
  }
  if (form.priceType !== "quote" && form.compareAtPrice !== "" && form.compareAtPrice !== null && form.compareAtPrice !== undefined &&
    (!Number.isFinite(compareAtPrice) || compareAtPrice <= 0 || compareAtPrice <= price)) {
    fields.compareAtPrice = "El precio tachado debe ser mayor que el precio de venta vigente.";
  }
  if (isReservableService(form) && Number(form.durationMinutes || 0) <= 0) {
    fields.durationMinutes = "Indicá cuánto dura el servicio para poder ofrecer turnos.";
  }
  if (isReservableService(form) && form.bookingProvider !== "calendly") {
    const capacityRaw = String(form.capacity ?? "").trim();
    const capacity = Number(capacityRaw);
    if (!capacityRaw || !Number.isFinite(capacity) || capacity < 1 || capacity > 10000) {
      fields.capacity = "Ingresá un cupo entre 1 y 10.000 por horario.";
    }
  }
  if (isReservableService(form) && form.bookingProvider === "calendly") {
    const calendlyUrl = String(form.calendlyUrl || "").trim();
    if (!calendlyUrl) {
      fields.calendlyUrl = "Pegá el enlace de Calendly que usarán tus clientes.";
    } else if (!isValidCalendlyUrl(calendlyUrl)) {
      fields.calendlyUrl = "Usá el enlace HTTPS de un evento real de Calendly, por ejemplo https://calendly.com/tu-negocio/turno.";
    }
  }

  return fields;
}

function validationFeedback(message, fields = {}) {
  return {
    kind: "validation",
    title: "Revisá los datos del ítem",
    message: String(message || "Hay datos que faltan o necesitan corrección."),
    fields,
  };
}

function saveErrorFeedback(error, { hasImages = false } = {}) {
  const status = Number(error?.status || 0);
  const code = String(error?.code || "").trim();
  const requestId = String(error?.requestId || "").trim();
  const message = hasImages
    ? describeImageUploadError(error, {
        action: "subir las imágenes del ítem",
        maxBytes: PRODUCT_IMAGE_MAX_SOURCE_BYTES,
      })
    : String(error?.message || "").trim();

  if ([400, 409, 413, 415, 422].includes(status)) {
    return {
      kind: "validation",
      title: "Hay algo para corregir",
      message: message || "Revisá los datos del ítem e intentá nuevamente.",
      fields: {},
    };
  }

  if ([401, 402, 403].includes(status)) {
    return {
      kind: "access",
      title: "No se puede guardar con este acceso",
      message: message || "Tu usuario o tu plan no permiten completar esta operación.",
      fields: {},
    };
  }

  if (error?.isOffline || error?.isNetworkError || error?.isTimeout) {
    const offline = error?.isOffline === true || (typeof navigator !== "undefined" && navigator.onLine === false);
    return {
      kind: "system",
      title: offline
        ? "Sin conexión a Internet"
        : error?.isTimeout
          ? "La operación sigue sin confirmación"
          : "Se interrumpió la comunicación",
      message: offline
        ? "Tus datos siguen en el formulario. Cuando vuelva Internet podés reintentar sin volver a cargar todo."
        : "Tus datos siguen en el formulario. WebProfit ya no interpreta una demora del servidor como un error de tus datos ni como falta de Internet.",
      detail: message || "",
      requestId,
      code,
      fields: {},
    };
  }

  return {
    kind: "system",
    title: "Error del sistema",
    message:
      status >= 500 || code === "INTERNAL_ERROR"
        ? "Los datos que cargaste no parecen ser el problema. El servidor no pudo guardar el ítem por una falla interna. Probá nuevamente; si se repite, usá el identificador de la solicitud para reportarlo."
        : "No pudimos comunicarnos correctamente con el servidor. Tus datos siguen en el formulario para que puedas volver a intentar.",
    detail: message && !/internal server error/i.test(message) ? message : "",
    requestId,
    code,
    fields: {},
  };
}

function reuseClassificationValue(value, options = []) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  return (options || []).find((option) =>
    sameClassification(option, normalized),
  ) || normalized;
}

function ItemFormHeading({ step, title, description }) {
  return (
    <Stack direction="row" spacing={1.2} alignItems="flex-start">
      <Box
        sx={{
          width: 32,
          height: 32,
          flex: "0 0 auto",
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: BRAND.dark,
          color: "#fff",
          fontWeight: 950,
          fontSize: 13,
          boxShadow: "0 7px 18px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.18)",
        }}
      >
        {step}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)", mt: 0.35, lineHeight: 1.45 }}>
          {description}
        </Typography>
      </Box>
    </Stack>
  );
}

const ITEM_FORM_STEPS = [
  "Tipo",
  "Nombre y descripción",
  "Categoría",
  "Precio",
  "Variantes",
  "Descuentos",
  "Uso",
  "Facturación",
  "Publicar",
  "Revisar",
];
const OPTIONAL_ITEM_STEPS = new Set([2, 4, 5, 6, 7, 8]);

function ItemOptionalStepNotice({ message = "Esta sección es opcional. Podés completarla ahora o seguir y volver después." }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: 1.35,
        py: 1.05,
        borderRadius: 2.5,
        borderColor: "rgba(243,90,58,.20)",
        bgcolor: "rgba(243,90,58,.035)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          label="Opcional"
          sx={{ fontWeight: 950, bgcolor: "rgba(243,90,58,.12)", color: BRAND.dark }}
        />
        <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.35 }}>
          {message}
        </Typography>
      </Stack>
    </Paper>
  );
}

function ItemWizardNav({ step, maxVisitedStep = step, onStepChange }) {
  return (
    <Box
      aria-label={`Paso ${step + 1} de ${ITEM_FORM_STEPS.length}`}
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${ITEM_FORM_STEPS.length}, minmax(0,1fr))`,
        gap: { xs: 0.38, md: 0.58 },
        width: "100%",
        minWidth: 0,
      }}
    >
      {ITEM_FORM_STEPS.map((label, index) => {
        const active = index === step;
        const visited = index <= maxVisitedStep;
        const clickable = visited && typeof onStepChange === "function";
        return (
          <Tooltip
            key={label}
            title={clickable ? `Ir a ${label}` : index > maxVisitedStep ? "Completá los pasos anteriores para llegar acá" : label}
            arrow
          >
            <span style={{ minWidth: 0 }}>
              <ButtonBase
                type="button"
                data-wp-item-step-nav={index}
                disabled={!clickable}
                onClick={() => clickable && onStepChange(index)}
                aria-current={active ? "step" : undefined}
                sx={{
                  width: "100%",
                  minWidth: 0,
                  minHeight: { xs: 34, md: 44 },
                  px: { xs: 0.2, md: 0.5 },
                  py: { xs: 0.5, md: 0.62 },
                  borderRadius: 2.2,
                  border: "1px solid",
                  borderColor: active
                    ? "rgba(243,90,58,.42)"
                    : visited
                      ? "rgba(46,125,50,.18)"
                      : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.07)",
                  bgcolor: active
                    ? "rgba(243,90,58,.09)"
                    : visited
                      ? "rgba(46,125,50,.045)"
                      : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.018)",
                  opacity: visited ? 1 : 0.58,
                  transition: "transform .16s ease, border-color .16s ease, background-color .16s ease, box-shadow .16s ease",
                  "&:hover": clickable ? {
                    transform: "translateY(-1px)",
                    borderColor: active ? "rgba(243,90,58,.55)" : "rgba(243,90,58,.26)",
                    boxShadow: "0 7px 18px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.07)",
                  } : undefined,
                }}
              >
                <Stack direction="row" spacing={0.42} alignItems="center" justifyContent="center" sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      width: { xs: 21, md: 22 },
                      height: { xs: 21, md: 22 },
                      flex: "0 0 auto",
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: active ? BRAND.orange : visited ? "success.main" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
                      color: active ? "#17181C" : visited ? "#fff" : BRAND.dark,
                      fontSize: 10.5,
                      fontWeight: 950,
                    }}
                  >
                    {visited && !active ? "✓" : index + 1}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: { xs: "none", md: "block" },
                      fontWeight: active ? 950 : 820,
                      color: BRAND.dark,
                      minWidth: 0,
                      fontSize: { md: 8.4, lg: 9.5 },
                      lineHeight: 1.05,
                      textAlign: "center",
                      whiteSpace: "normal",
                    }}
                  >
                    {label}
                  </Typography>
                </Stack>
              </ButtonBase>
            </span>
          </Tooltip>
        );
      })}
    </Box>
  );
}


export default function ProjectProductsPage() {
  const { projectId } = useParams();
  const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const compactLayout = useMediaQuery("(max-width:899.95px)", { noSsr: true });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { projects, activeProject, setActiveProject, loadProjects } =
    useProjects();
  const [products, setProducts] = React.useState([]);
  const [recipeSummaries, setRecipeSummaries] = React.useState([]);
  const [recipesLoading, setRecipesLoading] = React.useState(false);
  const [trialCapacity, setTrialCapacity] = React.useState(null);
  const [trialCapacityResolved, setTrialCapacityResolved] = React.useState(false);
  const [memberAccess, setMemberAccess] = React.useState(normalizeProjectAccess());
  const [form, setForm] = React.useState(emptyForm);
  const [editingProduct, setEditingProduct] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [productsLoaded, setProductsLoaded] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [processingImages, setProcessingImages] = React.useState(false);
  const [imagePreparationProgress, setImagePreparationProgress] = React.useState(null);
  const [imageUploadProgress, setImageUploadProgress] = React.useState(null);
  const [error, setError] = React.useState("");
  const [formFeedback, setFormFeedback] = React.useState(null);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const formContentRef = React.useRef(null);
  const [imageError, setImageError] = React.useState("");
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [previewUrls, setPreviewUrls] = React.useState([]);
  const previewUrlsRef = React.useRef([]);
  const autoCreateHandledRef = React.useRef("");
  const [existingImages, setExistingImages] = React.useState([]);
  const [removedImageIds, setRemovedImageIds] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [scannerOpen, setScannerOpen] = React.useState(false);
  const [createScannerOpen, setCreateScannerOpen] = React.useState(false);
  const [scanNotice, setScanNotice] = React.useState("");
  const [formCodeNotice, setFormCodeNotice] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [categoryFilter, setCategoryFilter] = React.useState("all");
  const [subcategoryFilter, setSubcategoryFilter] = React.useState("all");
  const [supplierFilter, setSupplierFilter] = React.useState("all");
  const [importOpen, setImportOpen] = React.useState(false);
  const [bulkPriceOpen, setBulkPriceOpen] = React.useState(false);
  const [bulkPricePercent, setBulkPricePercent] = React.useState("");
  const [bulkPricePreview, setBulkPricePreview] = React.useState(null);
  const [bulkPriceSaving, setBulkPriceSaving] = React.useState(false);
  const [bulkPriceError, setBulkPriceError] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(8);
  const [toolsOpen, setToolsOpen] = React.useState(false);
  const [advancedFormOpen, setAdvancedFormOpen] = React.useState(false);
  const [itemStep, setItemStep] = React.useState(0);
  const [itemMaxVisitedStep, setItemMaxVisitedStep] = React.useState(0);
  // WP-ITEM-CREATE-STEP-ZERO
  // STATUS: IMPLEMENTED / STATIC_PASS_TARGET / STAGING_PENDING
  // CONTRACT: every standard manual creation opens a neutral Step 0 first.
  // "normal" never starts the coach; "tutorial" always creates a fresh item tutorial session.
  // Editing existing items bypasses this choice completely.
  const [itemCreationMode, setItemCreationMode] = React.useState("normal");
  const [priceInputDraft, setPriceInputDraft] = React.useState("0");
  const [comparePriceInputDraft, setComparePriceInputDraft] = React.useState("");
  const itemLastStep = ITEM_FORM_STEPS.length - 1;
  const itemStepOptional = OPTIONAL_ITEM_STEPS.has(itemStep);

  React.useEffect(() => {
    if (!open) return undefined;
    const firstField = Object.keys(fieldErrors || {})[0];
    if (!firstField) return undefined;
    const timer = window.setTimeout(() => {
      const root = formContentRef.current;
      const field = root?.querySelector?.(`[name="${firstField}"]`);
      const target = field?.closest?.(".MuiFormControl-root") || field;
      target?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      field?.focus?.({ preventScroll: true });
    }, 90);
    return () => window.clearTimeout(timer);
  }, [advancedFormOpen, fieldErrors, open]);
  const [gastroStep, setGastroStep] = React.useState(0);
  const [gastroCategoryLinked, setGastroCategoryLinked] = React.useState(true);
  const [menuOrderOpen, setMenuOrderOpen] = React.useState(false);
  const [menuOrderSaving, setMenuOrderSaving] = React.useState(false);
  const [menuOrderError, setMenuOrderError] = React.useState("");
  const [menuOrderNotice, setMenuOrderNotice] = React.useState("");
  const [promotionDrafts, setPromotionDrafts] = React.useState([]);
  const [promotionLoading, setPromotionLoading] = React.useState(false);
  const [promotionError, setPromotionError] = React.useState("");
  const [savingStage, setSavingStage] = React.useState("");
  const [createdSupplyId, setCreatedSupplyId] = React.useState("");
  const initialFormSignatureRef = React.useRef("");
  const initialPromotionSignatureRef = React.useRef("[]");
  // BLOCK 1 telemetry: manual item-entry attempt, isolated from save/business logic.
  const itemEntryTelemetryRef = React.useRef({ operationId: "", started: false, finalized: false, attemptNumber: 0 });
  // BLOCK 8 · item academy telemetry. Structural only: no field values are emitted.
  const itemStepTelemetryRef = React.useRef({ step: -1, startedAt: 0 });

  React.useEffect(() => {
    // Step 0 is a routing choice, not a form step. Start per-section telemetry only
    // after the user actually enters either the normal or guided item form.
    if (!open || itemCreationMode === "" || productId(editingProduct)) {
      itemStepTelemetryRef.current = { step: -1, startedAt: 0 };
      return;
    }
    const now = Date.now();
    itemStepTelemetryRef.current = { step: itemStep, startedAt: now };
    reportActiveDashboardTutorialMetric("item_form_step_viewed", {
      stepIndex: itemStep,
      stepKey: ITEM_FORM_STEPS[itemStep] || `step_${itemStep + 1}`,
      optional: OPTIONAL_ITEM_STEPS.has(itemStep),
      sourceType: String(form.erpItemKind || "item"),
    });
  }, [editingProduct, form.erpItemKind, itemCreationMode, itemStep, open]);

  const project = React.useMemo(
    () =>
      projects.find(
        (item) => item.slug === projectId || item.id === projectId,
      ) || activeProject,
    [projects, projectId, activeProject],
  );
  const catalogCurrency = /^[A-Z]{3}$/.test(String(project?.commerce?.currency || "").trim().toUpperCase())
    ? String(project.commerce.currency).trim().toUpperCase()
    : "ARS";
  const itemPriceCurrency = /^[A-Z]{3}$/.test(String(form.priceCurrency || "").toUpperCase())
    ? String(form.priceCurrency).toUpperCase() : catalogCurrency;
  const productsEnabled = Boolean(
    project && isProjectModuleEnabled(project, "products"),
  );
  const gastronomicMenuEnabled = Boolean(
    project && isProjectCapabilityEnabled(project, "gastronomicMenu"),
  );
  const membershipsEnabled = Boolean(
    project && isProjectCapabilityEnabled(project, "memberships"),
  );
  const recipesEnabled = Boolean(
    project && isProjectCapabilityEnabled(project, "recipes"),
  );
  const requestedGastronomyView = String(searchParams.get("vista") || "").trim().toLowerCase();
  const menuView = requestedGastronomyView === "carta" && gastronomicMenuEnabled;
  const recipesView = requestedGastronomyView === "recetas" && recipesEnabled;
  // WP-TRIAL-UNLIMITED — STATIC_PASS / STAGING_PENDING
  // El trial ya no tiene tope de publicaciones. Conservamos `itemsUsed` sólo
  // como señal de onboarding/analítica; nunca debe convertirse en un bloqueo.
  // `isTrialProject` queda como fallback para despliegues desfasados donde el
  // backend todavía no exponga el payload `trial.unlimited`.
  const isTrial = trialCapacityResolved ? (trialCapacity !== null || isTrialProject(project)) : isTrialProject(project);
  const trialItemsUsed = Number(trialCapacity?.itemsUsed ?? products.length);
  const trialNeedsFirstPublication = isTrial && productsLoaded && products.length === 0;

  const canManageCosts =
    hasProjectPermission(memberAccess, "items.costs.manage") ||
    hasProjectPermission(memberAccess, "financial.costs.manage");
  const canManageAccounting = hasProjectPermission(
    memberAccess,
    "items.accounting.manage",
  );
  const canCreateItems = hasProjectPermission(memberAccess, "items.create");
  const canUpdateItems = hasProjectPermission(memberAccess, "items.update");
  const canDeleteItems = ["owner", "superadmin"].includes(String(memberAccess?.role || "").toLowerCase());
  const canManageStock = hasProjectPermission(memberAccess, "erp.stock.manage");

  const categories = React.useMemo(
    () => uniqueClassificationValues(products.map((item) => productCategory(item))),
    [products],
  );

  const gastronomicSections = React.useMemo(
    () => uniqueClassificationValues(
      products
        .filter((item) => item.gastronomicMenu?.enabled === true)
        .map((item) => String(item.gastronomicMenu?.section || "").trim() || productCategory(item)),
    ),
    [products],
  );

  const effectiveGastronomicSection = String(form.gastronomicSection || "").trim() || String(form.category || "").trim();
  const guidedGastronomicForm = gastronomicMenuEnabled && ["product", "supply"].includes(form.erpItemKind);
  const itemCreationChoiceOpen = Boolean(
    open && !editingProduct && !guidedGastronomicForm && itemCreationMode === "",
  );
  const gastronomicLastStep = form.erpItemKind === "supply" ? 3 : 5;

  const subcategoriesForCategory = React.useCallback(
    (category) =>
      uniqueClassificationValues(
        products
          .filter((item) => sameClassification(productCategory(item), category))
          .map((item) => productSubcategory(item)),
      ),
    [products],
  );

  const subcategories = React.useMemo(
    () =>
      categoryFilter === "all"
        ? []
        : subcategoriesForCategory(categoryFilter),
    [categoryFilter, subcategoriesForCategory],
  );

  const formSubcategories = React.useMemo(
    () => subcategoriesForCategory(form.category),
    [form.category, subcategoriesForCategory],
  );

  const supplierOptions = React.useMemo(() => {
    const values = products
      .map((item) => String(item?.inventory?.preferredSupplierId || "").trim())
      .filter(Boolean);
    return [...new Set(values)].sort();
  }, [products]);

  const filteredProducts = React.useMemo(() => {
    const query = normalizeSearchValue(searchTerm);

    return products.filter((product) => {
      const itemType = normalizeItemKind(product);
      const isActive = productIsActive(product);
      const category = productCategory(product);
      const subcategory = productSubcategory(product);
      const tags = tagsToText(product);
      const variantSearch = (product.variants || product.variations || [])
        .flatMap((variant) => [
          variant?.sku,
          variant?.barcode,
          ...Object.values(variant?.attributes || {}),
        ])
        .filter(Boolean)
        .join(" ");
      const searchableText = normalizeSearchValue(
        [
          productName(product),
          productDescription(product),
          category,
          subcategory,
          productCode(product),
          tags,
          variantSearch,
        ].join(" "),
      );

      const matchesSearch = !query || searchableText.includes(query);
      const matchesType = typeFilter === "all" || itemType === typeFilter;
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" ? isActive : !isActive);
      const matchesCategory =
        categoryFilter === "all" || sameClassification(category, categoryFilter);
      const matchesSubcategory =
        subcategoryFilter === "all" || sameClassification(subcategory, subcategoryFilter);
      const supplierId = String(product?.inventory?.preferredSupplierId || "").trim();
      const matchesSupplier = supplierFilter === "all" || supplierId === supplierFilter;
      const matchesGastronomyView = !menuView || product.gastronomicMenu?.enabled === true;

      return (
        matchesSearch &&
        matchesType &&
        matchesStatus &&
        matchesCategory &&
        matchesSubcategory &&
        matchesSupplier &&
        matchesGastronomyView
      );
    });
  }, [
    products,
    searchTerm,
    typeFilter,
    statusFilter,
    categoryFilter,
    subcategoryFilter,
    supplierFilter,
    menuView,
  ]);

  const paginatedProducts = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredProducts.slice(start, start + rowsPerPage);
  }, [filteredProducts, page, rowsPerPage]);

  const inventorySummary = React.useMemo(() => {
    const active = products.filter(productIsActive).length;
    const services = products.filter(
      (product) => normalizeItemKind(product) === "service",
    ).length;
    const internal = products.filter((product) =>
      ["supply", "expense", "asset"].includes(normalizeItemKind(product)),
    ).length;
    const lowStock = products.filter((product) => {
      const capabilities = productCapabilities(product);
      const threshold = Number(product.inventory?.reorderPoint ?? 5);
      return capabilities.stockTracked && productStock(product) <= threshold;
    }).length;

    return {
      total: products.length,
      active,
      services,
      internal,
      lowStock,
    };
  }, [products]);



  const hasActiveFilters = Boolean(
    searchTerm ||
    typeFilter !== "all" ||
    statusFilter !== "all" ||
    categoryFilter !== "all" ||
    subcategoryFilter !== "all" ||
    supplierFilter !== "all",
  );

  React.useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  React.useEffect(() => {
    let mounted = true;
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!mounted) return;
        setMemberAccess(normalizeProjectAccess(data.memberAccess || data.member || {}));
        if (data.project) setActiveProject(data.project);
      })
      .catch(() => {
        if (mounted) setMemberAccess(normalizeProjectAccess());
      });
    return () => {
      mounted = false;
    };
  }, [projectId, setActiveProject]);

  React.useEffect(() => {
    if (projectId && (!activeProject || activeProject.slug !== projectId)) {
      const found = projects.find(
        (item) => item.slug === projectId || item.id === projectId,
      );
      if (found) setActiveProject(found);
    }
  }, [projectId, projects, activeProject, setActiveProject]);

  React.useEffect(() => {
    previewUrlsRef.current = previewUrls;
  }, [previewUrls]);

  React.useEffect(
    () => () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );

  const loadProducts = React.useCallback(async () => {
    if (!projectId || !project || !productsEnabled) {
      setLoading(false);
      setProductsLoaded(true);
      return;
    }
    try {
      setProductsLoaded(false);
      setLoading(true);
      setError("");
      const data = await productsApi.list(projectId);
      const nextProducts = Array.isArray(data?.products) ? data.products : [];
      setProducts(nextProducts);
      setTrialCapacity(data?.trial || null);
      setTrialCapacityResolved(true);
      window.dispatchEvent(
        new CustomEvent(TRIAL_PRODUCTS_EVENT, {
          detail: { projectId, count: nextProducts.length },
        }),
      );
    } catch (err) {
      setProducts([]);
      setTrialCapacity(null);
      setTrialCapacityResolved(false);
      setError(err.message || "No se pudieron cargar las publicaciones");
    } finally {
      setLoading(false);
      setProductsLoaded(true);
    }
  }, [projectId, project, productsEnabled]);

  React.useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const refreshRecipeSummaries = React.useCallback(async () => {
    if (!projectId || !recipesEnabled) {
      setRecipeSummaries([]);
      return;
    }
    try {
      setRecipesLoading(true);
      const data = await recipesApi.list(projectId);
      setRecipeSummaries(Array.isArray(data?.recipes) ? data.recipes : []);
    } catch (err) {
      setRecipeSummaries([]);
      setError((current) => current || err.message || "No se pudieron cargar las recetas");
    } finally {
      setRecipesLoading(false);
    }
  }, [projectId, recipesEnabled]);

  React.useEffect(() => {
    refreshRecipeSummaries();
  }, [refreshRecipeSummaries]);

  const recipeSummaryByProduct = React.useMemo(
    () => new Map(recipeSummaries.map((recipe) => [String(recipe.productId), recipe])),
    [recipeSummaries],
  );

  const openRecipeForProduct = React.useCallback((product) => {
    const id = productId(product);
    if (!id) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("vista", "recetas");
    nextParams.set("producto", id);
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const clearRecipeSelection = React.useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (!nextParams.has("producto")) return;
    nextParams.delete("producto");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const manageRecipeSupplies = React.useCallback(() => {
    setTypeFilter("supply");
    setStatusFilter("all");
    setSearchTerm("");
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("vista");
    nextParams.delete("producto");
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  React.useEffect(() => {
    setPage(0);
  }, [
    searchTerm,
    typeFilter,
    statusFilter,
    categoryFilter,
    subcategoryFilter,
    rowsPerPage,
  ]);

  React.useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredProducts.length / rowsPerPage) - 1,
    );
    if (page > maxPage) setPage(maxPage);
  }, [filteredProducts.length, page, rowsPerPage]);

  const resetUploads = React.useCallback(() => {
    previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];
    setSelectedFiles([]);
    setPreviewUrls([]);
    setExistingImages([]);
    setRemovedImageIds([]);
    setImagePreparationProgress(null);
    setImageUploadProgress(null);
    setImageError("");
  }, []);

  React.useEffect(() => {
    if (!open) return;
    initialFormSignatureRef.current = JSON.stringify(form);
    initialPromotionSignatureRef.current = JSON.stringify([]);
    // Sólo al abrir: los cambios posteriores quedan detectados como borrador.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadPromotionDrafts = React.useCallback(async (targetProductId) => {
    if (!targetProductId || !gastronomicMenuEnabled) {
      setPromotionDrafts([]);
      setPromotionError("");
      return;
    }
    setPromotionLoading(true);
    setPromotionError("");
    try {
      const response = await promotionsApi.list(projectId);
      const related = (response?.promotions || []).filter((promotion) =>
        (promotion.lines || []).some((line) => String(line.productId) === String(targetProductId)),
      );
      const drafts = related.map(promotionToDraft);
      setPromotionDrafts(drafts);
      initialPromotionSignatureRef.current = JSON.stringify(drafts);
    } catch (err) {
      setPromotionDrafts([]);
      setPromotionError(err?.message || "No se pudieron cargar las promociones de este producto.");
    } finally {
      setPromotionLoading(false);
    }
  }, [gastronomicMenuEnabled, projectId]);

  const handleClose = () => {
    if (saving || processingImages) return;
    const dirty = Boolean(
      selectedFiles.length
      || removedImageIds.length
      || JSON.stringify(form) !== initialFormSignatureRef.current
      || JSON.stringify(promotionDrafts) !== initialPromotionSignatureRef.current
    );
    if (dirty && !createdSupplyId) {
      const confirmed = window.confirm("Hay cambios sin guardar. ¿Querés cerrar y descartarlos?");
      if (!confirmed) return;
    }
    setOpen(false);
    setFormFeedback(null);
    setFieldErrors({});
    setFormCodeNotice("");
    setCreateScannerOpen(false);
    setGastroStep(0);
    setItemStep(0);
    setItemMaxVisitedStep(0);
    setItemCreationMode("normal");
    setGastroCategoryLinked(true);
    setPromotionDrafts([]);
    setPromotionError("");
    setSavingStage("");
    setCreatedSupplyId("");
    resetUploads();
  };

  const openCreateWithType = React.useCallback(
    (itemKind = "product") => {
      if (!canCreateItems) {
        setError("Tu rol no tiene permiso para crear ítems.");
        return;
      }
      const normalizedKind = ITEM_KIND_LABELS[itemKind] ? itemKind : "product";
      const capabilities = capabilitiesForKind(normalizedKind);

      // Step 0 owns the decision between a plain form and the guided form.
      // If creation was reached from the catalog block of a running tutorial,
      // pause that session so its overlay cannot cover this neutral choice screen.
      const currentTutorial = loadDashboardTutorialState(project);
      const currentTutorialBlock = currentTutorial?.planKeys?.[currentTutorial?.step]
        || currentTutorial?.blockKey
        || currentTutorial?.stageKey
        || "";
      if (currentTutorial?.active && currentTutorialBlock === "catalog") {
        pauseDashboardTutorial(project, "item_create_step_zero");
      }

      setEditingProduct(null);
      setForm({
        ...emptyForm,
        priceCurrency: catalogCurrency,
        erpItemKind: normalizedKind,
        itemType: normalizedKind === "service" ? "service" : "product",
        ...capabilities,
        requiresBooking: Boolean(capabilities.reservable),
        requiresShipping: Boolean(capabilities.shippable),
        attributes: [],
        ...(gastronomicMenuEnabled && normalizedKind === "product"
          ? { category: "", gastronomicEnabled: true, gastronomicAvailable: true }
          : gastronomicMenuEnabled && normalizedKind === "supply"
            ? { category: "Insumos", gastronomicEnabled: false }
            : { gastronomicEnabled: false }),
      });
      setGastroStep(0);
      setItemStep(0);
      setItemMaxVisitedStep(0);
      // Gastronomy keeps its dedicated wizard. Standard items always expose Step 0.
      setItemCreationMode(gastronomicMenuEnabled && ["product", "supply"].includes(normalizedKind) ? "normal" : "");
      setPriceInputDraft(formatCatalogMoneyValue(0, catalogCurrency));
      setComparePriceInputDraft("");
      setGastroCategoryLinked(gastronomicMenuEnabled && normalizedKind === "product");
      setPromotionDrafts([]);
      setPromotionError("");
      setCreatedSupplyId("");
      resetUploads();
      setError("");
      setFormFeedback(null);
      setFieldErrors({});
      setFormCodeNotice("");
      setAdvancedFormOpen(false);
      setOpen(true);
    },
    [canCreateItems, catalogCurrency, gastronomicMenuEnabled, project, resetUploads],
  );

  const trackItemCreationMode = React.useCallback((mode, repeated = false) => {
    if (!project) return;
    activityApi.trackPanel({
      action: "onboarding_item_create_mode_selected",
      projectId: String(project?.id || project?._id || ""),
      projectSlug: String(project?.slug || ""),
      projectName: String(project?.name || ""),
      path: typeof window !== "undefined" ? window.location.pathname : "",
      journey: {
        channel: "panel",
        path: typeof window !== "undefined" ? window.location.pathname : "",
        step: 0,
      },
      metadata: {
        analyticsOnly: true,
        telemetryVersion: 1,
        tutorialKey: "item",
        stageKey: "catalog",
        stepIndex: 0,
        itemCreateMode: mode,
        repeatedTutorial: repeated === true,
        sourceType: String(form.erpItemKind || "item"),
      },
    }).catch(() => null);
  }, [form.erpItemKind, project]);

  const chooseNormalItemCreation = React.useCallback(() => {
    // Intentionally no tutorial session: this path is the fastest route for experienced users.
    setItemCreationMode("normal");
    trackItemCreationMode("normal", false);
  }, [trackItemCreationMode]);

  const chooseGuidedItemCreation = React.useCallback(() => {
    if (!project) {
      setItemCreationMode("normal");
      return;
    }
    const previous = loadDashboardTutorialState(project);
    const previousBlock = previous?.planKeys?.[previous?.step] || previous?.blockKey || previous?.stageKey || "";
    const canResumeCatalog = previous?.paused === true && previousBlock === "catalog" && previous?.tutorialKey === "welcome";
    const repeated = Boolean(
      previous?.tutorialKey === "item"
      || previous?.completedBlocks?.includes?.("catalog")
      || (previous?.completedAt && previousBlock === "catalog"),
    );

    if (canResumeCatalog) {
      resumeDashboardTutorial(project, "item_step_zero_resume_catalog");
    } else {
      startDashboardTutorial(project, "item", repeated ? "item_step_zero_repeat" : "item_step_zero");
    }
    engageDashboardTutorialTarget(project, "catalog", {
      reason: canResumeCatalog
        ? "item_step_zero_resume_catalog"
        : repeated
          ? "item_step_zero_repeat"
          : "item_step_zero",
    });
    setItemCreationMode("tutorial");
    trackItemCreationMode("tutorial", repeated);
  }, [project, trackItemCreationMode]);

  const handleOpenCreate = React.useCallback(() => {
    openCreateWithType(recipesView ? "supply" : "product");
  }, [openCreateWithType, recipesView]);

  React.useEffect(() => {
    const shouldCreate = searchParams.get("crear") === "1";
    if (!shouldCreate || !project || !productsEnabled || loading || !productsLoaded) return;

    const requestKey = `${projectId}:${searchParams.toString()}`;
    if (autoCreateHandledRef.current === requestKey) return;
    autoCreateHandledRef.current = requestKey;

    const requestedKind = ITEM_KIND_LABELS[searchParams.get("tipo")]
      ? searchParams.get("tipo")
      : "product";
    openCreateWithType(requestedKind);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("crear");
    nextParams.delete("tipo");
    setSearchParams(nextParams, { replace: true });
  }, [
    loading,
    productsLoaded,
    openCreateWithType,
    productsEnabled,
    project,
    projectId,
    searchParams,
    setSearchParams,
  ]);

  const handleOpenEdit = (product) => {
    if (!canUpdateItems) {
      setError("Tu rol no tiene permiso para editar ítems.");
      return;
    }
    setEditingProduct(product);
    setItemCreationMode("normal");
    const itemType = normalizeItemType(product);
    const erpItemKind = normalizeItemKind(product);
    const capabilities = productCapabilities(product);
    setForm({
      itemType,
      erpItemKind,
      name: product.name || product.nombre || "",
      description: product.description || product.descripcion || "",
      category: product.category || product.categoria || "General",
      subcategory: product.subcategory || product.subcategoria || "",
      gastronomicEnabled: product.gastronomicMenu?.enabled === true,
      gastronomicAvailable: product.gastronomicMenu?.available !== false,
      gastronomicShortDescription: product.gastronomicMenu?.shortDescription || "",
      gastronomicDietaryNotes: product.gastronomicMenu?.dietaryNotes || "",
      gastronomicSection: product.gastronomicMenu?.section || "",
      gastronomicSectionOrder: String(product.gastronomicMenu?.sectionOrder ?? 0),
      gastronomicSortOrder: String(product.gastronomicMenu?.sortOrder ?? 0),
      gastronomicFeatured: product.gastronomicMenu?.featured === true,
      code: product.code || product.codigo || "",
      unit: product.unit || product.unidad || "",
      priceType: normalizePriceType(product),
      priceCurrency: product.priceCurrency || product.precioMoneda || catalogCurrency,
      price: String(product.price ?? product.precio ?? 0),
      compareAtPrice: product.compareAtPrice ?? product.precioTachadoWeb ?? "",
      stock: String(product.stock ?? 0),
      durationMinutes: String(
        product.durationMinutes ?? product.duracionMinutos ?? "",
      ),
      modality: product.modality || product.modalidad || "",
      requiresBooking: Boolean(capabilities.reservable),
      requiresShipping: Boolean(capabilities.shippable),
      bookingProvider:
        product.bookingProvider === "calendly"
          ? "calendly"
          : Boolean(capabilities.reservable)
            ? "webprofit"
            : "none",
      calendlyUrl: product.calendlyUrl || "",
      calendlyDisplayMode: product.calendlyDisplayMode === "inline" ? "inline" : "popup",
      bookingConfirmationMode:
        product.bookingConfirmationMode ||
        (product.confirmacionTurno === "automatica" ? "automatic" : "manual"),
      bookingPaymentMode:
        product.bookingPaymentMode || product.pagoReserva || "none",
      bookingDepositType:
        product.bookingDepositType || product.tipoSena || "fixed",
      bookingDepositValue: String(
        product.bookingDepositValue ?? product.valorSena ?? 0,
      ),
      capacity: String(product.capacity ?? product.capacidad ?? 1),
      bufferBeforeMinutes: String(
        product.bufferBeforeMinutes ?? product.bufferAntesMinutos ?? 0,
      ),
      bufferAfterMinutes: String(
        product.bufferAfterMinutes ?? product.bufferDespuesMinutos ?? 0,
      ),
      serviceLocation:
        product.serviceLocation || product.ubicacionServicio || "",
      meetingUrl: product.meetingUrl || product.enlaceVideollamada || "",
      bookingInstructions:
        product.bookingInstructions || product.instruccionesReserva || "",
      cancellationWindowHours: String(
        product.cancellationWindowHours ?? product.cancelacionHoras ?? 0,
      ),
      tags: tagsToText(product),
      attributes: attributesToRows(product.attributes),
      hasVariants: Boolean(
        product.hasVariants ??
          ((product.variantAttributes || product.variantOptions || []).length &&
            (product.variants || product.variations || []).length),
      ),
      variantAttributes: variantAttributesToForm(product),
      variants: variantsToForm(product),
      quantityPriceTiers: quantityPriceTiersToForm(product),
      active: Boolean(product.active ?? product.activo ?? true),
      membershipEligible: product.operational?.membershipEligible === true,
      membershipPeriodValue: String(product.membershipPlan?.periodValue ?? 1),
      membershipPeriodUnit: product.membershipPlan?.periodUnit || "months",
      membershipReferencePrice: String(product.membershipPlan?.referencePrice ?? product.price ?? product.precio ?? 0),
      membershipUsageEnabled: product.membershipPlan?.usageEnabled === true,
      membershipIncludedUses: product.membershipPlan?.includedUses == null ? "" : String(product.membershipPlan.includedUses),
      membershipUsageLabel: product.membershipPlan?.usageLabel || "usos",
      ...capabilities,
      lastCost: String(product.costing?.lastCost ?? 0),
      averageCost: String(product.costing?.averageCost ?? 0),
      standardCost: String(product.costing?.standardCost ?? 0),
      costingMethod: product.costing?.costingMethod || "last_cost",
      costCurrency: product.costing?.currency || "ARS",
      allowNegativeStock: product.inventory?.allowNegativeStock === true,
      minimumStock: String(product.inventory?.minimumStock ?? 0),
      reorderPoint: String(product.inventory?.reorderPoint ?? 0),
      inventoryLocation: product.inventory?.location || "",
      salesAccountCode: product.accounting?.salesAccountCode || "SALES",
      purchaseAccountCode: product.accounting?.purchaseAccountCode || "PURCHASES",
      inventoryAccountCode: product.accounting?.inventoryAccountCode || "INVENTORY",
      costAccountCode: product.accounting?.costAccountCode || "COST_OF_GOODS",
      expenseAccountCode: product.accounting?.expenseAccountCode || "OTHER_EXPENSE",
      taxProfileCode: product.tax?.profileCode || "",
      taxRate: String(product.tax?.rate ?? 0),
      taxIncludedInPrice: product.tax?.includedInPrice === true,
    });
    resetUploads();
    setFormFeedback(null);
    setFieldErrors({});
    setFormCodeNotice("");

    const resolvedUrls = getProductImageUrls(product);
    const images = (product.images || [])
      .map((img, index) => ({
        identifier:
          (typeof img === "string"
            ? img
            : img?.path || img?.filename || img?.url) || "",
        url:
          resolvedUrls[index] ||
          (typeof img === "string" ? img : img?.url || img?.path) ||
          "",
      }))
      .filter((img) => img.identifier && img.url);
    setExistingImages(images.slice(0, MAX_IMAGES));
    setGastroStep(0);
    setItemStep(0);
    setItemMaxVisitedStep(itemLastStep);
    setPriceInputDraft(formatCatalogMoneyValue(product.price ?? product.precio ?? 0,
      product.priceCurrency || product.precioMoneda || catalogCurrency));
    setComparePriceInputDraft(formatCatalogMoneyValue(product.compareAtPrice ?? product.precioTachadoWeb ?? "",
      product.priceCurrency || product.precioMoneda || catalogCurrency));
    // En edición no sincronizamos categoría y sección de forma implícita:
    // los documentos históricos conservan exactamente su clasificación actual.
    setGastroCategoryLinked(false);
    setPromotionDrafts([]);
    setPromotionError("");
    setCreatedSupplyId("");
    setAdvancedFormOpen(Boolean(
      String(product.code || product.codigo || "").trim()
      || String(product.subcategory || product.subcategoria || "").trim()
      || String(product.unit || product.unidad || "").trim()
      || (product.variants || product.variations || []).length
      || tagsToText(product)
      || attributesToRows(product.attributes).length
      || Number(product.costing?.lastCost || product.costing?.averageCost || product.costing?.standardCost || 0) > 0
      || String(product.tax?.profileCode || "").trim()
    ));
    setOpen(true);
    if (erpItemKind === "product" && gastronomicMenuEnabled) loadPromotionDrafts(productId(product));
  };

  const handleRemoveExistingImage = (identifier) => {
    setExistingImages((prev) =>
      prev.filter((img) => img.identifier !== identifier),
    );
    setRemovedImageIds((prev) =>
      prev.includes(identifier) ? prev : [...prev, identifier],
    );
    setImageError("");
  };

  const handleFilesChange = async (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    if (!picked.length) return;

    const available = Math.max(
      0,
      MAX_IMAGES - existingImages.length - selectedFiles.length,
    );
    if (!available) {
      setImageError(`La publicación ya tiene ${MAX_IMAGES} imágenes.`);
      return;
    }

    const accepted = picked.slice(0, available);
    if (picked.length > available) {
      setImageError(
        `Se tomarán ${available} archivo${available === 1 ? "" : "s"}. El máximo total es ${MAX_IMAGES}.`,
      );
    } else {
      setImageError("");
    }

    try {
      setProcessingImages(true);
      setImagePreparationProgress(0);
      const optimized = await optimizeProductImages(accepted, {
        onProgress: ({ percent }) => setImagePreparationProgress(percent),
      });
      const urls = optimized.map((file) => URL.createObjectURL(file));
      setSelectedFiles((prev) => [...prev, ...optimized]);
      setPreviewUrls((prev) => [...prev, ...urls]);
    } catch (err) {
      setImageError(err.message || "No se pudieron preparar las imágenes.");
    } finally {
      setProcessingImages(false);
      setImagePreparationProgress(null);
    }
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles((prev) =>
      prev.filter((_, itemIndex) => itemIndex !== index),
    );
    setPreviewUrls((prev) => {
      const url = prev[index];
      if (url) URL.revokeObjectURL(url);
      return prev.filter((_, itemIndex) => itemIndex !== index);
    });
    setImageError("");
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (name && fieldErrors[name]) {
      setFieldErrors((current) => {
        const next = { ...current };
        delete next[name];
        return next;
      });
    }
  };

  const handleGeneralItemKindChange = React.useCallback((nextKind) => {
    if (!ITEM_KIND_LABELS[nextKind]) return;
    setForm((current) => {
      const nextCapabilities = capabilitiesForKind(nextKind);
      const supportsVariants = supportsVariantsForKind(nextKind);
      const isService = nextKind === "service";

      return {
        ...current,
        erpItemKind: nextKind,
        itemType: isService ? "service" : "product",
        ...nextCapabilities,
        requiresBooking: Boolean(nextCapabilities.reservable),
        requiresShipping: Boolean(nextCapabilities.shippable),
        stock: nextCapabilities.stockTracked ? current.stock : "0",
        durationMinutes: isService ? current.durationMinutes : "",
        modality: isService ? current.modality : "",
        bookingConfirmationMode: isService ? current.bookingConfirmationMode : "manual",
        bookingPaymentMode: isService ? current.bookingPaymentMode : "none",
        bookingDepositType: isService ? current.bookingDepositType : "fixed",
        bookingDepositValue: isService ? current.bookingDepositValue : "0",
        capacity: isService ? current.capacity : "1",
        bufferBeforeMinutes: isService ? current.bufferBeforeMinutes : "0",
        bufferAfterMinutes: isService ? current.bufferAfterMinutes : "0",
        serviceLocation: isService ? current.serviceLocation : "",
        meetingUrl: isService ? current.meetingUrl : "",
        bookingInstructions: isService ? current.bookingInstructions : "",
        cancellationWindowHours: isService ? current.cancellationWindowHours : "0",
        hasVariants: supportsVariants ? current.hasVariants : false,
        variantAttributes: supportsVariants ? current.variantAttributes : [],
        variants: supportsVariants ? current.variants : [],
      };
    });
  }, []);

  const handleGastronomicRoleChange = React.useCallback((nextKind) => {
    const normalizedKind = ITEM_KIND_LABELS[nextKind] ? nextKind : "product";
    if (editingProduct && normalizedKind !== form.erpItemKind) {
      const confirmed = window.confirm(
        "Cambiar el tipo puede modificar capacidades operativas del ítem. Los datos existentes se conservarán siempre que sigan siendo compatibles. ¿Querés continuar?",
      );
      if (!confirmed) return;
    }

    setForm((current) => {
      const nextCapabilities = capabilitiesForKind(normalizedKind);
      const isService = normalizedKind === "service";
      const supportsVariants = supportsVariantsForKind(normalizedKind);
      const isGuidedProduct = normalizedKind === "product";
      const isSupply = normalizedKind === "supply";
      return {
        ...current,
        erpItemKind: normalizedKind,
        itemType: isService ? "service" : "product",
        ...nextCapabilities,
        requiresBooking: Boolean(nextCapabilities.reservable),
        requiresShipping: Boolean(nextCapabilities.shippable),
        gastronomicEnabled: isGuidedProduct ? true : isSupply ? false : current.gastronomicEnabled,
        gastronomicAvailable: isGuidedProduct ? current.gastronomicAvailable !== false : current.gastronomicAvailable,
        ...(!editingProduct && isSupply ? { category: "Insumos", subcategory: "" } : {}),
        ...(!editingProduct && isGuidedProduct && current.erpItemKind !== "product" ? { category: "", subcategory: "" } : {}),
        stock: nextCapabilities.stockTracked ? current.stock : "0",
        durationMinutes: isService ? current.durationMinutes : "",
        modality: isService ? current.modality : "",
        hasVariants: supportsVariants ? current.hasVariants : false,
        variantAttributes: supportsVariants ? current.variantAttributes : [],
        variants: supportsVariants ? current.variants : [],
      };
    });
    setGastroStep(0);
    if (!editingProduct && normalizedKind === "product") setGastroCategoryLinked(true);
    if (normalizedKind !== "product") setGastroCategoryLinked(false);
  }, [editingProduct, form.erpItemKind]);

  const handleGastronomicSectionChange = React.useCallback((value) => {
    const nextSection = String(value || "");
    const matching = products.filter((item) => sameClassification(
      String(item.gastronomicMenu?.section || "").trim() || productCategory(item),
      nextSection,
    ));
    const existingSectionOrder = matching.length
      ? Number(matching[0]?.gastronomicMenu?.sectionOrder || 0)
      : Math.max(0, ...products.map((item) => Number(item.gastronomicMenu?.sectionOrder || 0))) + 10;
    const nextSortOrder = matching.length
      ? Math.max(0, ...matching.map((item) => Number(item.gastronomicMenu?.sortOrder || 0))) + 10
      : 0;
    setForm((current) => ({
      ...current,
      gastronomicSection: nextSection,
      ...(!editingProduct || !sameClassification(current.gastronomicSection || current.category, nextSection)
        ? { gastronomicSectionOrder: String(existingSectionOrder), gastronomicSortOrder: String(nextSortOrder) }
        : {}),
      ...(gastroCategoryLinked
        ? {
            category: nextSection || current.category,
            subcategory: sameClassification(current.category, nextSection) ? current.subcategory : "",
          }
        : {}),
    }));
    if (fieldErrors.category) {
      setFieldErrors((current) => {
        const next = { ...current };
        delete next.category;
        return next;
      });
    }
  }, [editingProduct, fieldErrors.category, gastroCategoryLinked, products]);

  const handleGastronomicCategoryChange = React.useCallback((value) => {
    const nextCategory = String(value || "");
    setGastroCategoryLinked(false);
    setForm((current) => ({
      ...current,
      category: nextCategory,
      subcategory: sameClassification(current.category, nextCategory) ? current.subcategory : "",
    }));
  }, []);

  const handleAttributeChange = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      attributes: prev.attributes.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [key]: value } : row,
      ),
    }));
  };

  const addAttribute = () => {
    setForm((prev) => ({
      ...prev,
      attributes: [...prev.attributes, { label: "", value: "" }].slice(0, 30),
    }));
  };

  const removeAttribute = (index) => {
    setForm((prev) => ({
      ...prev,
      attributes: prev.attributes.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const goToItemStep = React.useCallback((nextStep) => {
    setFormFeedback(null);
    setFieldErrors({});
    const resolvedStep = Math.max(0, Math.min(itemLastStep, nextStep));
    setItemStep(resolvedStep);
    setItemMaxVisitedStep((current) => Math.max(current, resolvedStep));
    window.setTimeout(() => {
      formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
    }, 0);
  }, [itemLastStep]);

  const reportItemValidationBlock = React.useCallback((reasonCode, errorCount = 1) => {
    const state = itemEntryTelemetryRef.current;
    if (!state.started) return;
    reportActiveDashboardTutorialMetric("item_validation_blocked", {
      operationId: state.operationId,
      attemptNumber: state.attemptNumber,
      sourceType: String(form?.erpItemKind || "item"),
      reasonCode,
      errorCount: Math.max(1, Number(errorCount || 1)),
      itemCount: 1,
    });
  }, [form?.erpItemKind]);

  const handleItemNext = React.useCallback(() => {
    const stepStartedAt = Number(itemStepTelemetryRef.current?.startedAt || 0);
    const reportStepCompleted = () => reportActiveDashboardTutorialMetric("item_form_step_completed", {
      stepIndex: itemStep,
      stepKey: ITEM_FORM_STEPS[itemStep] || `step_${itemStep + 1}`,
      optional: OPTIONAL_ITEM_STEPS.has(itemStep),
      sourceType: String(form.erpItemKind || "item"),
      activeStepMs: stepStartedAt ? Math.max(0, Date.now() - stepStartedAt) : 0,
    });
    if (itemStep === 1) {
      const allErrors = validateBasicItemFields(form);
      const nameErrors = {};
      if (allErrors.name) nameErrors.name = allErrors.name;
      if (Object.keys(nameErrors).length) {
        reportItemValidationBlock("name", Object.keys(nameErrors).length);
        setFieldErrors(nameErrors);
        setFormFeedback(validationFeedback("Poné un nombre para seguir.", nameErrors));
        return;
      }
    }

    if (itemStep === 3) {
      const allErrors = validateBasicItemFields(form);
      const priceErrors = {};
      if (allErrors.price) priceErrors.price = allErrors.price;
      if (allErrors.compareAtPrice) priceErrors.compareAtPrice = allErrors.compareAtPrice;
      if (Object.keys(priceErrors).length) {
        reportItemValidationBlock("price", Object.keys(priceErrors).length);
        setFieldErrors(priceErrors);
        setFormFeedback(validationFeedback("Revisá el precio para seguir.", priceErrors));
        return;
      }
    }

    if (itemStep === 4) {
      const allErrors = validateBasicItemFields(form);
      const optionErrors = {};
      ["durationMinutes", "capacity", "calendlyUrl"].forEach((field) => {
        if (allErrors[field]) optionErrors[field] = allErrors[field];
      });
      if (Object.keys(optionErrors).length) {
        reportItemValidationBlock("options", Object.keys(optionErrors).length);
        setFieldErrors(optionErrors);
        setFormFeedback(validationFeedback("Revisá esta configuración para seguir.", optionErrors));
        return;
      }
      const variantError = validateVariantForm(form);
      if (variantError) {
        reportItemValidationBlock("variants", 1);
        setFieldErrors({});
        setFormFeedback(validationFeedback(variantError));
        return;
      }
    }

    if (itemStep === 5) {
      const quantityDiscountError = validateQuantityDiscountRows(form);
      if (quantityDiscountError) {
        reportItemValidationBlock("quantity_discounts", 1);
        setFieldErrors({});
        setFormFeedback(validationFeedback(quantityDiscountError));
        return;
      }
    }

    reportStepCompleted();
    goToItemStep(itemStep + 1);
  }, [form, goToItemStep, itemStep, reportItemValidationBlock]);

  const handleItemSkip = React.useCallback(() => {
    const stepStartedAt = Number(itemStepTelemetryRef.current?.startedAt || 0);
    reportActiveDashboardTutorialMetric("item_form_step_skipped", {
      stepIndex: itemStep,
      stepKey: ITEM_FORM_STEPS[itemStep] || `step_${itemStep + 1}`,
      optional: OPTIONAL_ITEM_STEPS.has(itemStep),
      sourceType: String(form.erpItemKind || "item"),
      activeStepMs: stepStartedAt ? Math.max(0, Date.now() - stepStartedAt) : 0,
    });
    goToItemStep(itemStep + 1);
  }, [form.erpItemKind, goToItemStep, itemStep]);

  React.useEffect(() => {
    // The creation attempt begins after Step 0. This keeps tutorial analytics honest:
    // opening the chooser alone is not counted as starting the item form.
    const creating = open && itemCreationMode !== "" && !productId(editingProduct);
    const state = itemEntryTelemetryRef.current;
    if (creating && !state.started) {
      const attemptNumber = Number(state.attemptNumber || 0) + 1;
      itemEntryTelemetryRef.current = {
        operationId: `item-entry:${Date.now()}:${Math.random().toString(36).slice(2, 9)}`,
        started: true,
        finalized: false,
        attemptNumber,
      };
      reportActiveDashboardTutorialMetric("item_entry_started", {
        operationId: itemEntryTelemetryRef.current.operationId,
        attemptNumber,
        sourceType: String(form?.erpItemKind || "item"),
        itemCount: 1,
      });
      return;
    }
    if (!open && state.started && !state.finalized) {
      reportActiveDashboardTutorialMetric("item_entry_abandoned", {
        operationId: state.operationId,
        attemptNumber: state.attemptNumber,
        sourceType: String(form?.erpItemKind || "item"),
        itemCount: 1,
        processedCount: 0,
      });
      itemEntryTelemetryRef.current = { ...state, started: false, finalized: true };
    }
    if (!open && state.finalized) {
      itemEntryTelemetryRef.current = { ...state, started: false };
    }
  }, [editingProduct, form?.erpItemKind, itemCreationMode, open]);



  const handleSubmit = async (event) => {
    event.preventDefault();
    if (processingImages) return;

    const editingId = productId(editingProduct);
    const creating = !editingId;
    // WP-ONBOARDING-FIRST-ITEM-HANDOFF
    // STATUS: IMPLEMENTED / STATIC_PASS_TARGET / STAGING_PENDING
    // CONTRACT: only the very first standard catalog item can trigger the web
    // handoff. Existing save, stock, booking and persistence behavior stays intact.
    const shouldLaunchFirstItemHandoff = Boolean(
      creating
      && products.length === 0
      && form.erpItemKind !== "supply"
      && project
      && isProjectModuleEnabled(project, "landing")
    );
    if ((creating && !canCreateItems) || (!creating && !canUpdateItems)) {
      setFormFeedback({
        kind: "access",
        title: "No tenés permiso para guardar",
        message: "Tu rol no tiene permiso para guardar este ítem.",
        fields: {},
      });
      formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
      return;
    }
    const basicFieldErrors = validateBasicItemFields(form, {
      requireCategory: guidedGastronomicForm && form.erpItemKind === "product",
    });
    if (Object.keys(basicFieldErrors).length) {
      reportItemValidationBlock("basic_fields", Object.keys(basicFieldErrors).length);
      if (Object.keys(basicFieldErrors).some((field) => !["name", "category", "price", "compareAtPrice"].includes(field))) {
        setAdvancedFormOpen(true);
      }
      setFieldErrors(basicFieldErrors);
      setFormFeedback(
        validationFeedback(
          "Completá los campos marcados antes de guardar.",
          basicFieldErrors,
        ),
      );
      if (guidedGastronomicForm) {
        setGastroStep((basicFieldErrors.price || basicFieldErrors.compareAtPrice) && !basicFieldErrors.name && !basicFieldErrors.category ? 2 : basicFieldErrors.category && !basicFieldErrors.name ? 1 : 0);
      } else {
        const optionFields = ["durationMinutes", "capacity", "calendlyUrl"];
        setItemStep(
          Object.keys(basicFieldErrors).some((field) => optionFields.includes(field))
            ? 4
            : (basicFieldErrors.price || basicFieldErrors.compareAtPrice) && !basicFieldErrors.name
              ? 3
              : 1,
        );
      }
      formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
      return;
    }

    const variantError = validateVariantForm(form);
    if (variantError) {
      setAdvancedFormOpen(true);
      setFormFeedback(validationFeedback(variantError));
      if (guidedGastronomicForm && form.erpItemKind === "product") setGastroStep(3);
      if (!guidedGastronomicForm) setItemStep(4);
      formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
      return;
    }

    const quantityDiscountError = validateQuantityDiscountRows(form);
    if (quantityDiscountError) {
      setAdvancedFormOpen(true);
      setFormFeedback(validationFeedback(quantityDiscountError));
      if (guidedGastronomicForm && form.erpItemKind === "product") setGastroStep(4);
      if (!guidedGastronomicForm) setItemStep(5);
      formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
      return;
    }

    if (form.erpItemKind === "product") {
      const promotionDraftError = validatePromotionDrafts(promotionDrafts, editingId);
      if (promotionDraftError) {
        reportItemValidationBlock("promotions", 1);
        setAdvancedFormOpen(true);
        setFormFeedback(validationFeedback(promotionDraftError));
        if (guidedGastronomicForm) setGastroStep(4);
        formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
        return;
      }
    }

    try {
      setSaving(true);
      setSavingStage("product");
      setError("");
      setFormFeedback(null);
      setFieldErrors({});
      const normalizedCategory =
        reuseClassificationValue(form.category, categories) || "General";
      const normalizedSubcategory = reuseClassificationValue(
        form.subcategory,
        subcategoriesForCategory(normalizedCategory),
      );
      const payload = normalizeProductPayload(
        {
          ...form,
          category: normalizedCategory,
          subcategory: normalizedSubcategory,
        },
        variantImageOptions(existingImages, previewUrls),
        { membershipsEnabled },
      );
      if (gastronomicMenuEnabled) {
        payload.gastronomicMenu = {
          enabled: Boolean(form.gastronomicEnabled),
          available: Boolean(form.gastronomicAvailable),
          shortDescription: String(form.gastronomicShortDescription || "").trim(),
          dietaryNotes: String(form.gastronomicDietaryNotes || "").trim(),
          section: reuseClassificationValue(form.gastronomicSection, gastronomicSections),
          sectionOrder: Number(form.gastronomicSectionOrder || 0),
          sortOrder: Number(form.gastronomicSortOrder || 0),
          featured: Boolean(form.gastronomicFeatured),
        };
      }
      let savedProduct = null;
      const uploadOptions = selectedFiles.length
        ? {
            onUploadProgress: (progressEvent) => {
              const percent = uploadProgressPercent(progressEvent);
              if (percent !== null) setImageUploadProgress(percent);
            },
          }
        : {};
      setImageUploadProgress(selectedFiles.length ? 0 : null);
      if (editingId) {
        if (removedImageIds.length) payload.removeImages = removedImageIds;
        const response = await productsApi.update(
          projectId,
          editingId,
          payload,
          selectedFiles,
          uploadOptions,
        );
        savedProduct = response?.product || null;
      } else {
        const response = await productsApi.create(
          projectId,
          payload,
          selectedFiles,
          uploadOptions,
        );
        savedProduct = response?.product || null;
      }
      const savedId = productId(savedProduct) || editingId;
      if (creating) {
        const telemetry = itemEntryTelemetryRef.current;
        reportActiveDashboardTutorialMetric("item_entry_completed", {
          operationId: telemetry.operationId,
          attemptNumber: telemetry.attemptNumber,
          sourceType: String(form.erpItemKind || "item"),
          itemCount: 1,
          processedCount: 1,
          errorCount: 0,
        });
        itemEntryTelemetryRef.current = { ...telemetry, finalized: true };
      }
      const promotionsChanged = JSON.stringify(promotionDrafts) !== initialPromotionSignatureRef.current;
      if (form.erpItemKind === "product" && promotionDrafts.length && promotionsChanged) {
        setSavingStage("promotions");
        let persistedDrafts = [...promotionDrafts];
        try {
          for (let index = 0; index < persistedDrafts.length; index += 1) {
            const draft = persistedDrafts[index];
            if (draft._delete) {
              if (draft.id) await promotionsApi.remove(projectId, draft.id);
              persistedDrafts = persistedDrafts.filter((_, currentIndex) => currentIndex !== index);
              index -= 1;
              setPromotionDrafts(persistedDrafts);
              continue;
            }
            const promotionPayload = promotionPayloadFromDraft(draft, savedId);
            const response = draft.id
              ? await promotionsApi.update(projectId, draft.id, promotionPayload)
              : await promotionsApi.create(projectId, promotionPayload);
            const persisted = promotionToDraft(response?.promotion || { ...promotionPayload, id: draft.id });
            persistedDrafts[index] = persisted;
            setPromotionDrafts([...persistedDrafts]);
          }
          initialPromotionSignatureRef.current = JSON.stringify(persistedDrafts);
        } catch (promotionSaveError) {
          setPromotionDrafts(persistedDrafts);
          setEditingProduct(savedProduct || { ...editingProduct, id: savedId, _id: savedId });
          initialFormSignatureRef.current = JSON.stringify(form);
          setPromotionError(promotionSaveError?.message || "El producto se guardó, pero una promoción no pudo guardarse.");
          setFormFeedback({
            kind: "warning",
            title: "Producto guardado · promoción pendiente",
            message: "El producto ya existe y no se volverá a crear. Lo que alcanzó a guardarse no se duplicará; revisá lo pendiente e intentá nuevamente.",
            detail: promotionSaveError?.message || "No se pudo guardar la promoción.",
            fields: {},
          });
          setGastroStep(4);
          resetUploads();
          await loadProducts();
          formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
          return;
        }
      }

      resetUploads();
      await loadProducts();

      if (creating && form.erpItemKind === "supply") {
        setEditingProduct(savedProduct || { id: savedId, _id: savedId });
        setCreatedSupplyId(savedId);
        initialFormSignatureRef.current = JSON.stringify(form);
        initialPromotionSignatureRef.current = JSON.stringify(promotionDrafts);
        setGastroStep(3);
        setFormFeedback({
          kind: "success",
          title: "Insumo creado",
          message: canManageStock
            ? "Ya podés registrar su stock inicial desde Movimientos."
            : "El insumo quedó creado. Un usuario con permiso de stock puede cargar la existencia inicial desde Movimientos.",
          fields: {},
        });
        return;
      }

      setOpen(false);

      if (shouldLaunchFirstItemHandoff) {
        // The first catalog win should become visible immediately. We route to the
        // existing Página workspace (the editor antechamber), never directly into
        // a long editor flow. A reservable service keeps its agenda destination
        // encoded so the antechamber can return the user there without losing it.
        const savedId = productId(savedProduct);
        const nextParams = new URLSearchParams({ momento: "primer-item" });
        if (creating && isReservableService(form) && form.bookingProvider !== "calendly" && savedId) {
          nextParams.set("continuar", `/dashboard/proyectos/${projectId}/agenda?servicio=${savedId}&nuevo=1`);
        }
        reportActiveDashboardTutorialMetric("first_item_handoff_started", {
          sourceType: String(form.erpItemKind || "item"),
          itemCount: 1,
        });
        navigate(`/dashboard/proyectos/${projectId}/pagina?${nextParams.toString()}`);
        return;
      }

      if (creating && isReservableService(form) && form.bookingProvider !== "calendly") {
        const savedId = productId(savedProduct);
        navigate(
          `/dashboard/proyectos/${projectId}/agenda${savedId ? `?servicio=${savedId}&nuevo=1` : ""}`,
        );
      }
    } catch (err) {
      if (creating && !itemEntryTelemetryRef.current.finalized) {
        const telemetry = itemEntryTelemetryRef.current;
        reportActiveDashboardTutorialMetric("item_entry_failed", {
          operationId: telemetry.operationId,
          attemptNumber: telemetry.attemptNumber,
          sourceType: String(form.erpItemKind || "item"),
          itemCount: 1,
          processedCount: 0,
          errorCount: 1,
        });
      }
      setFormFeedback(
        saveErrorFeedback(err, { hasImages: selectedFiles.length > 0 }),
      );
      formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
    } finally {
      setSaving(false);
      setSavingStage("");
      setImageUploadProgress(null);
    }
  };

  const handleDelete = async (product) => {
    if (!canDeleteItems) {
      setError("Tu rol no tiene permiso para eliminar ítems.");
      return;
    }
    const label =
      normalizeItemType(product) === "service" ? "servicio" : "producto";
    const name = productName(product);
    const ok = window.confirm(
      `¿Eliminar el ${label} "${name}" del catálogo?\n\nSólo el dueño puede hacerlo. Si este ítem aparece en movimientos anteriores, esos movimientos conservarán su nombre, código, precio y demás snapshots históricos.`,
    );
    if (!ok) return;
    try {
      setError("");
      await productsApi.remove(projectId, productId(product), { mode: "owner_delete_preserve_history" });
      reportActiveDashboardTutorialMetric("owner_item_deleted", {
        sourceType: normalizeItemType(product),
        preservedHistory: true,
      });
      await loadProducts();
    } catch (err) {
      if (err?.code === "SYSTEM_ITEM_OWNER_DELETE_REQUIRES_ZERO_STOCK") {
        setError("Este ítem todavía tiene stock real. Corregí o anulá los movimientos que lo dejaron con existencia y después volvé a eliminarlo.");
        return;
      }
      setError(err.message || "No se pudo eliminar el ítem");
    }
  };

  const handleScannedItemCode = React.useCallback(async (rawCode) => {
    const code = String(rawCode || "").trim();
    if (!code) return;
    const data = await productsApi.lookupCode(projectId, code);
    const found = data?.product;
    if (!found) throw new Error(`No se encontró ningún ítem con el código ${code}.`);

    setProducts((current) => {
      const foundId = productId(found);
      const exists = current.some((item) => productId(item) === foundId);
      return exists
        ? current.map((item) => (productId(item) === foundId ? found : item))
        : [found, ...current];
    });
    setTypeFilter("all");
    setStatusFilter("all");
    setCategoryFilter("all");
    setSubcategoryFilter("all");
    setSupplierFilter("all");
    setSearchTerm(code);
    setPage(0);
    setScanNotice(`Encontrado: ${productName(found)}${productCode(found) ? ` · ${productCode(found)}` : ""}`);
  }, [projectId]);

  const handleScannedCreateCode = React.useCallback(async (rawCode) => {
    const code = String(rawCode || "").trim();
    if (!code) return;
    try {
      await productsApi.lookupCode(projectId, code);
      throw new Error(`El código ${code} ya está asignado a otro ítem o variante.`);
    } catch (error) {
      if (error?.code !== "ITEM_CODE_NOT_FOUND" && error?.status !== 404) throw error;
    }

    setForm((current) => ({ ...current, code }));
    setFieldErrors((current) => ({ ...current, code: "" }));
    setFormCodeNotice(`Código capturado: ${code}. Se guardará únicamente al crear este ítem.`);
  }, [projectId]);

  const handleDownloadCodes = React.useCallback((items, filename = "webprofit-codigos.pdf") => {
    try {
      const result = downloadBarcodeLabelsPdf(items, filename);
      setScanNotice(`PDF generado: ${result.labels} etiqueta${result.labels === 1 ? "" : "s"} lista${result.labels === 1 ? "" : "s"} para imprimir.`);
    } catch (error) {
      setError(error?.message || "No se pudieron generar las etiquetas.");
    }
  }, []);

  const handleDownloadCurrentFormCode = React.useCallback(() => {
    const syntheticProduct = {
      name: form.name || "Ítem",
      code: form.code,
      variants: Array.isArray(form.variants) ? form.variants : [],
    };
    const base = String(form.name || form.code || "item").trim() || "item";
    handleDownloadCodes([syntheticProduct], `webprofit-${base}-codigos.pdf`);
  }, [form.code, form.name, form.variants, handleDownloadCodes]);

  const openBulkPriceAdjustment = () => {
    setBulkPricePercent("");
    setBulkPricePreview(null);
    setBulkPriceError("");
    setBulkPriceOpen(true);
  };

  const bulkPricePayload = (dryRun) => ({
    percentage: Number(bulkPricePercent),
    dryRun,
    productIds: filteredProducts
      .map((item) => productId(item))
      .filter((value) => /^[a-f0-9]{24}$/i.test(String(value || "")))
      .slice(0, 500),
  });

  const previewBulkPriceAdjustment = async () => {
    const percentage = Number(bulkPricePercent);
    if (!Number.isFinite(percentage) || percentage === 0 || percentage <= -100) {
      setBulkPriceError("Ingresá un porcentaje válido distinto de 0 y mayor a -100.");
      return;
    }
    try {
      setBulkPriceSaving(true);
      setBulkPriceError("");
      const result = await productsApi.bulkAdjustPrices(projectId, bulkPricePayload(true));
      setBulkPricePreview(result);
    } catch (requestError) {
      setBulkPriceError(requestError?.message || "No se pudo calcular la vista previa.");
    } finally {
      setBulkPriceSaving(false);
    }
  };

  const applyBulkPriceAdjustment = async () => {
    try {
      setBulkPriceSaving(true);
      setBulkPriceError("");
      const result = await productsApi.bulkAdjustPrices(projectId, bulkPricePayload(false));
      await loadProducts();
      setBulkPriceOpen(false);
      setScanNotice(`Precios actualizados: ${result?.modified ?? result?.matched ?? 0} ítem(s).`);
    } catch (requestError) {
      setBulkPriceError(requestError?.message || "No se pudo aplicar el ajuste masivo.");
    } finally {
      setBulkPriceSaving(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setScanNotice("");
    setTypeFilter("all");
    setStatusFilter("all");
    setCategoryFilter("all");
    setSubcategoryFilter("all");
    setSupplierFilter("all");
  };

  const handleSaveMenuOrder = React.useCallback(async (updates = []) => {
    if (!canUpdateItems || menuOrderSaving) return;
    setMenuOrderSaving(true);
    setMenuOrderError("");
    setMenuOrderNotice("");
    try {
      for (const update of updates) {
        if (!update.id) continue;
        await productsApi.update(projectId, update.id, { gastronomicMenu: update.gastronomicMenu });
      }
      await loadProducts();
      setMenuOrderNotice("Orden de la Carta guardado.");
      setMenuOrderOpen(false);
    } catch (orderError) {
      await loadProducts();
      setMenuOrderError(orderError?.message || "No se pudo guardar todo el orden. Se recargó el estado real para evitar mostrar un éxito falso.");
    } finally {
      setMenuOrderSaving(false);
    }
  }, [canUpdateItems, loadProducts, menuOrderSaving, projectId]);

  const openStockMovement = React.useCallback((product, kind = "") => {
    const id = productId(product);
    if (!id || !canManageStock) return;
    const params = new URLSearchParams({
      nuevo: "1",
      itemId: String(id),
      origen: "catalogo-stock",
    });
    if (kind) params.set("tipo", kind);
    navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos?${params.toString()}`);
  }, [canManageStock, navigate, projectId]);

  if (project && !productsEnabled) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Ítems del negocio"
        description="Este módulo unifica catálogo, servicios, insumos, gastos, stock y conceptos internos."
      />
    );
  }

  if (loading && !productsLoaded) {
    return <DashboardLoadingState label="Cargando ítems del negocio…" minHeight={420} />;
  }

  return (
    <Stack
      spacing={menuView || recipesView ? { xs: 1, md: 1.15 } : 0}
      sx={{
        pb: menuView || recipesView ? { xs: 2, md: 2.5 } : 0,
        ...(!menuView && !recipesView ? {
          mx: { xs: "-12px", sm: "-20px", lg: "-24px" },
          mt: "-10px",
        } : {}),
      }}
    >
      {menuView || recipesView ? (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ minHeight: 44 }}
      >
        <Stack direction="row" spacing={0.8} alignItems="center" useFlexGap flexWrap="wrap" sx={{ minWidth: 0 }}>
          <Typography
            variant={compactLayout ? "h6" : "h5"}
            sx={{ color: BRAND.dark, fontWeight: 1000, letterSpacing: -0.35, lineHeight: 1 }}
          >
            {menuView ? "Carta" : recipesView ? "Recetas e insumos" : "Ítems"}
          </Typography>
          {!recipesView && productsLoaded ? (
            <Chip size="small" label={`${products.length} ${products.length === 1 ? "ítem" : "ítems"}`} sx={{ height: 24, fontWeight: 900 }} />
          ) : null}
          {!menuView && !recipesView && inventorySummary.lowStock > 0 ? (
            <Chip
              size="small"
              label={`${inventorySummary.lowStock} con stock bajo`}
              sx={{ height: 24, fontWeight: 900, color: "#B23D28", bgcolor: "rgba(243,90,58,.09)" }}
            />
          ) : null}
          {isTrial && products.length > 0 ? (
            <Chip
              size="small"
              label={`Trial · ${trialItemsUsed} ${trialItemsUsed === 1 ? "ítem" : "ítems"}`}
              variant="outlined"
              sx={{ height: 24, fontWeight: 850 }}
            />
          ) : null}
        </Stack>

        {(menuView || canCreateItems) && !recipesView ? (
          <Stack direction="row" spacing={0.75} sx={{ alignSelf: { xs: "stretch", sm: "auto" } }}>
            {menuView ? (
              <Button
                size="small"
                variant="outlined"
                disabled={loading || !productsLoaded}
                onClick={() => { setMenuOrderError(""); setMenuOrderOpen(true); }}
                sx={{ minHeight: 38, borderRadius: 2.2, textTransform: "none", fontWeight: 900, px: 1.5, color: BRAND.dark }}
              >
                Ordenar
              </Button>
            ) : null}
            {canCreateItems ? (
              <>
                {!trialNeedsFirstPublication ? (
                  <Button
                    data-wp-tutorial-target="items-create-primary"
                    size="small"
                    startIcon={<AddRoundedIcon />}
                    variant="contained"
                    disabled={loading || !productsLoaded}
                    onClick={handleOpenCreate}
                    sx={{
                      minHeight: 38,
                      bgcolor: BRAND.orange,
                      borderRadius: 2.2,
                      textTransform: "none",
                      fontWeight: 950,
                      px: 1.7,
                      boxShadow: "0 8px 20px rgba(243,90,58,.18)",
                      "&:hover": { bgcolor: "#DB4B2F" },
                    }}
                  >
                    {menuView ? "Nuevo" : "Nuevo ítem"}
                  </Button>
                ) : null}
                <Button
                  size="small"
                  startIcon={<MoreHorizRoundedIcon />}
                  variant="outlined"
                  disabled={loading || !productsLoaded}
                  onClick={() => setToolsOpen((current) => !current)}
                  aria-expanded={toolsOpen}
                  sx={{ minHeight: 38, borderRadius: 2.2, textTransform: "none", fontWeight: 900, px: 1.35, color: BRAND.dark }}
                >
                  {compactLayout ? "Más" : "Opciones"}
                </Button>
              </>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
      ) : null}
      {!recipesView && canCreateItems ? (
        <Collapse in={toolsOpen} timeout="auto" unmountOnExit>
          <Paper variant="outlined" sx={{ p: { xs: 1.25, sm: 1.5 }, borderRadius: 3, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.018)" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} useFlexGap flexWrap="wrap">
              <Button
                startIcon={<UploadRoundedIcon />}
                variant="outlined"
                onClick={() => { setError(""); setImportOpen(true); }}
                sx={{ minHeight: 44, textTransform: "none", fontWeight: 900 }}
              >
                Importar ítems
              </Button>
              <Button
                startIcon={<DownloadRoundedIcon />}
                variant="outlined"
                onClick={() => handleDownloadCodes(filteredProducts, "webprofit-codigos-items.pdf")}
                disabled={!filteredProducts.length}
                sx={{ minHeight: 44, textTransform: "none", fontWeight: 900 }}
              >
                Descargar códigos
              </Button>
              {canUpdateItems ? (
                <Button
                  startIcon={<TrendingUpRoundedIcon />}
                  variant="outlined"
                  onClick={openBulkPriceAdjustment}
                  disabled={!filteredProducts.length}
                  sx={{ minHeight: 44, textTransform: "none", fontWeight: 900 }}
                >
                  Ajuste masivo de precios
                </Button>
              ) : null}
            </Stack>
          </Paper>
        </Collapse>
      ) : null}

      {error && !open ? <Alert severity="error">{error}</Alert> : null}
      {menuOrderNotice ? <Alert severity="success" onClose={() => setMenuOrderNotice("")} sx={{ borderRadius: 3 }}>{menuOrderNotice}</Alert> : null}

      {menuView ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          La carta usa los mismos ítems del negocio. Desde acá gestionás disponibilidad, sección y presentación gastronómica.
        </Alert>
      ) : null}

      {recipesView ? (
        <RecipesWorkspace
          projectId={projectId}
          products={products}
          loadingProducts={loading}
          canCreateItems={canCreateItems}
          canUpdateItems={canUpdateItems}
          recipes={recipeSummaries}
          recipesLoading={recipesLoading}
          onRefreshRecipes={refreshRecipeSummaries}
          onCreateSupply={() => openCreateWithType("supply")}
          onManageSupplies={manageRecipeSupplies}
          initialProductId={searchParams.get("producto") || ""}
          onClearInitialProduct={clearRecipeSelection}
        />
      ) : (
        <>
      {/* [WP-STABLE][ITEMS-TABLE-FIRST]
          STATUS: STATIC_PASS / STAGING_PENDING. La tabla/estado vacío vuelve a ser
          el primer plano; no apilamos una segunda “misión” encima del catálogo. */}
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          borderRadius: 0,
          border: 0,
          borderTop: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.09)",
          borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.09)",
          overflow: "hidden",
          bgcolor: "var(--wp-panel-surface, #fff)",
          boxShadow: "none",
        }}
      >
        <Box sx={{ px: { xs: 0.8, md: 1 }, py: 0.75 }}>
          <ResponsiveListControls
            search={(
              <TextField
                value={searchTerm}
                onChange={(event) => { setSearchTerm(event.target.value); setScanNotice(""); }}
                placeholder="Buscar por nombre, código, categoría o etiqueta"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Escanear código">
                        <IconButton
                          size="small"
                          onClick={() => setScannerOpen(true)}
                          aria-label="Escanear código para buscar un ítem"
                          sx={{ width: 40, height: 40, color: BRAND.dark }}
                        >
                          <QrCodeScannerRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiOutlinedInput-root": { minHeight: 40, borderRadius: 2.2, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.025)" } }}
              />
            )}
            activeFilterCount={[
              typeFilter !== "all",
              statusFilter !== "all",
              categoryFilter !== "all",
              subcategoryFilter !== "all",
              supplierFilter !== "all",
            ].filter(Boolean).length}
            onClear={clearFilters}
            filterLabel="Filtros"
            drawerTitle="Filtrar ítems"
            mobileActionsBelow
            sx={{
              flexWrap: "nowrap",
              gap: 0.65,
              "& > .MuiBox-root:first-of-type": {
                flex: "1 1 240px",
                minWidth: 170,
                maxWidth: 310,
              },
              "& > .MuiAutocomplete-root": { flexShrink: 1 },
              "& > .MuiStack-root:last-of-type": {
                ml: "auto",
                flexShrink: 0,
              },
            }}
            actions={(
              <Stack direction="row" spacing={0.6} alignItems="center" useFlexGap flexWrap={{ xs: "wrap", md: "nowrap" }}>
                {!menuView && canCreateItems ? (
                  <Button
                    data-wp-tutorial-target="items-create-primary"
                    size="small"
                    startIcon={<AddRoundedIcon />}
                    variant="contained"
                    disabled={loading || !productsLoaded}
                    onClick={handleOpenCreate}
                    sx={{
                      minHeight: { xs: 40, md: 38 },
                      px: 1.5,
                      flex: { xs: "1 1 148px", md: "0 0 auto" },
                      borderRadius: 2.2,
                      textTransform: "none",
                      fontWeight: 1000,
                      whiteSpace: "nowrap",
                      color: "#17181C",
                      bgcolor: BRAND.orange,
                      boxShadow: "0 7px 18px rgba(243,90,58,.22)",
                      "&:hover": { bgcolor: "#FF714F", boxShadow: "0 9px 22px rgba(243,90,58,.28)" },
                    }}
                  >
                    Nuevo ítem
                  </Button>
                ) : null}
                {!menuView && canCreateItems ? (
                  <Button
                    size="small"
                    startIcon={<MoreHorizRoundedIcon />}
                    variant="outlined"
                    disabled={loading || !productsLoaded}
                    onClick={() => setToolsOpen((current) => !current)}
                    aria-expanded={toolsOpen}
                    sx={{
                      minHeight: { xs: 40, md: 38 },
                      px: 1.15,
                      flex: { xs: "1 1 118px", md: "0 0 auto" },
                      borderRadius: 2.2,
                      textTransform: "none",
                      fontWeight: 900,
                      whiteSpace: "nowrap",
                      color: BRAND.dark,
                    }}
                  >
                    Opciones
                  </Button>
                ) : null}
                <Chip
                  size="small"
                  label={`${filteredProducts.length}${hasActiveFilters ? `/${products.length}` : ""}`}
                  sx={{ height: 30, fontWeight: 900 }}
                />
                <Tooltip title="Actualizar listado">
                  <span>
                    <IconButton
                      onClick={loadProducts}
                      disabled={loading}
                      aria-label="Actualizar ítems"
                      sx={{ width: 38, height: 38, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.14)", borderRadius: 2.2, color: BRAND.dark }}
                    >
                      <RefreshRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}
          >
            <SearchPicker
              options={["all", ...Object.keys(ITEM_KIND_LABELS)]}
              value={typeFilter}
              onChange={(_event, value) => setTypeFilter(value || "all")}
              getOptionLabel={(value) => value === "all" ? "Todos" : ({
                product: "Productos",
                service: "Servicios",
                supply: "Insumos",
                expense: "Gastos",
                asset: "Activos",
                charge: "Cargos",
              }[value] || value)}
              disableClearable
              label="Tipo"
              placeholder="Buscar tipo"
              helperText=""
              maxVisibleOptions={7}
              fullWidth={false}
              sx={{ width: 108, minWidth: 88, flex: "0 1 108px" }}
            />
            <SearchPicker
              options={["all", "active", "inactive"]}
              value={statusFilter}
              onChange={(_event, value) => setStatusFilter(value || "all")}
              getOptionLabel={(value) => ({ all: "Todos", active: "Activos", inactive: "Ocultos" }[value] || value)}
              disableClearable
              label="Estado"
              placeholder="Buscar estado"
              helperText=""
              maxVisibleOptions={3}
              fullWidth={false}
              sx={{ width: 108, minWidth: 88, flex: "0 1 108px" }}
            />
            <SearchPicker
              options={["all", ...categories]}
              value={categoryFilter}
              onChange={(_event, value) => {
                setCategoryFilter(value || "all");
                setSubcategoryFilter("all");
              }}
              getOptionLabel={(value) => value === "all" ? "Todas" : value}
              disableClearable
              label="Categoría"
              placeholder="Buscar categoría"
              helperText=""
              maxVisibleOptions={8}
              fullWidth={false}
              sx={{ width: 132, minWidth: 104, flex: "0 1 132px" }}
            />
            <SearchPicker
              options={["all", ...subcategories]}
              value={subcategoryFilter}
              onChange={(_event, value) => setSubcategoryFilter(value || "all")}
              getOptionLabel={(value) => value === "all" ? "Todas" : value}
              disableClearable
              disabled={categoryFilter === "all" || !subcategories.length}
              label="Subcategoría"
              placeholder="Buscar subcategoría"
              helperText=""
              maxVisibleOptions={8}
              fullWidth={false}
              sx={{ width: 132, minWidth: 104, flex: "0 1 132px" }}
            />
            {supplierOptions.length ? (
              <SearchPicker
                options={["all", ...supplierOptions]}
                value={supplierFilter}
                onChange={(_event, value) => setSupplierFilter(value || "all")}
                getOptionLabel={(value) => value === "all" ? "Todos" : `Proveedor · ${String(value).slice(-8)}`}
                disableClearable
                label="Proveedor"
                placeholder="Buscar proveedor"
                helperText=""
                maxVisibleOptions={8}
                fullWidth={false}
                sx={{ width: 132, minWidth: 104, flex: "0 1 132px" }}
              />
            ) : null}
          </ResponsiveListControls>
        </Box>

        <Divider />
        {loading ? <WebProfitSpinner size={24} sx={{ mx: "auto", my: 1 }} /> : null}
        {scanNotice ? (
          <Alert severity="success" onClose={() => setScanNotice("")} sx={{ mx: { xs: 2, md: 2.5 }, mt: 1.5 }}>
            {scanNotice}
          </Alert>
        ) : null}


        <Box sx={{ display: { xs: "block", md: "none" }, py: .15 }}>
          {!loading && paginatedProducts.length === 0 ? (
            <Stack alignItems="center" spacing={1.15} sx={{ px: 2, py: 5, textAlign: "center" }}>
              <Box sx={{ width: 58, height: 58, borderRadius: 3, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.05)", display: "grid", placeItems: "center" }}>
                <Inventory2RoundedIcon sx={{ fontSize: 30, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }} />
              </Box>
              <Typography sx={{ fontWeight: 1000, color: BRAND.dark }}>
                {menuView && filteredProducts.length === 0
                  ? "Todavía no hay ítems en la carta"
                  : products.length
                    ? "No encontramos coincidencias"
                    : isTrial
                      ? "Tu catálogo está listo para empezar"
                      : "Todavía no hay publicaciones"}
              </Typography>
              <Typography variant="body2" sx={{ maxWidth: 340, color: "text.secondary" }}>
                {menuView && filteredProducts.length === 0
                  ? "Creá un ítem desde esta vista o editá uno existente para incluirlo en la carta gastronómica."
                  : products.length
                    ? "Probá cambiando la búsqueda o quitando algún filtro."
                    : isTrial
                      ? "Elegí qué vendés y te llevamos directo al formulario correcto."
                      : "Creá el primer producto o servicio para comenzar el inventario."}
              </Typography>
              {products.length ? (
                <Button onClick={clearFilters} variant="outlined" sx={{ minHeight: 44, borderRadius: 2.2, textTransform: "none", fontWeight: 900 }}>Limpiar filtros</Button>
              ) : canCreateItems ? (
                <Button onClick={handleOpenCreate} variant="contained" startIcon={<AddRoundedIcon />} sx={{ minHeight: 44, borderRadius: 2.2, bgcolor: BRAND.orange, color: "#17181C", textTransform: "none", fontWeight: 950 }}>
                  Crear ítem
                </Button>
              ) : null}
            </Stack>
          ) : (
            paginatedProducts.map((product) => {
              const recipeSummary = recipeSummaryByProduct.get(String(productId(product))) || null;
              return (
                <MobileCatalogItemCard
                  key={productId(product)}
                  product={product}
                  catalogCurrency={catalogCurrency}
                  canUpdateItems={canUpdateItems}
                  canDeleteItems={canDeleteItems}
                  canManageStock={canManageStock}
                  recipesEnabled={recipesEnabled}
                  recipeSummary={recipeSummary}
                  onEdit={handleOpenEdit}
                  onStock={openStockMovement}
                  onRecipe={openRecipeForProduct}
                  onDelete={handleDelete}
                />
              );
            })
          )}
        </Box>

        <TableContainer sx={{ display: { xs: "none", md: "block" }, minHeight: 0, p: 0, maxWidth: "100%", overflowX: "auto" }}>
          <ResponsiveOperationalTable
            stickyHeader
            sx={{
              minWidth: 980,
              "& .wp-items-actions-cell": {
                position: "sticky",
                right: 0,
                zIndex: 3,
                minWidth: 132,
                bgcolor: "var(--wp-panel-surface, #fff)",
                boxShadow: "-8px 0 14px rgba(0,0,0,.07)",
              },
              "& thead .wp-items-actions-cell": {
                zIndex: 6,
                bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)",
              },
              "& thead .MuiTableCell-root": { py: 0 },
              "& tbody .MuiTableCell-root": { py: 0.55, px: 1.35 },
            }}
            aria-label="Listado de ítems"
            mobileLabel="Ítems del negocio"
            collapseAfter={4}
            mobileEssentialLabels={["Publicación", "Precio", "Stock / Duración", "Estado"]}
          >
            <TableHead sx={{ display: { xs: "table-header-group", md: "none" } }}>
              <TableRow>
                {[
                  "Publicación",
                  "Tipo",
                  "SKU / Código",
                  "Categoría",
                  "Precio",
                  "Stock / Duración",
                  "Estado",
                  "Acciones",
                ].map((label) => (
                  <TableCell
                    key={label}
                    align={label === "Acciones" ? "right" : "left"}
                    className={label === "Acciones" ? "wp-items-actions-cell" : undefined}
                    sx={{
                      bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)",
                      color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
                      fontSize: 12,
                      fontWeight: 950,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                      borderBottom: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {!loading && paginatedProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 9, borderBottom: 0 }}>
                    <Stack alignItems="center" spacing={1.5}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 3,
                          bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.05)",
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <Inventory2RoundedIcon
                          sx={{ fontSize: 34, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 950, color: BRAND.dark }}
                      >
                        {menuView && filteredProducts.length === 0
                          ? "Todavía no hay ítems en la carta"
                          : products.length
                            ? "No encontramos coincidencias"
                            : isTrial
                              ? "Tu catálogo está listo para empezar"
                              : "Todavía no hay publicaciones"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
                          textAlign: "center",
                        }}
                      >
                        {menuView && filteredProducts.length === 0
                          ? "Creá un ítem desde esta vista o editá uno existente para incluirlo en la carta gastronómica."
                          : products.length
                            ? "Probá cambiando la búsqueda o quitando algún filtro."
                            : isTrial
                              ? "Elegí qué vendés y te llevamos directo al formulario correcto."
                              : "Creá el primer producto o servicio para comenzar el inventario."}
                      </Typography>
                      {products.length ? (
                        <Button
                          onClick={clearFilters}
                          sx={{ textTransform: "none", fontWeight: 850 }}
                        >
                          Limpiar filtros
                        </Button>
                      ) : isTrial && canCreateItems ? (
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
                          <Button
                            onClick={() => openCreateWithType("product")}
                            variant="contained"
                            startIcon={<Inventory2RoundedIcon />}
                            sx={{
                              bgcolor: BRAND.orange,
                              textTransform: "none",
                              fontWeight: 900,
                              borderRadius: 2.5,
                            }}
                          >
                            Cargar producto
                          </Button>
                          <Button
                            onClick={() => openCreateWithType("service")}
                            variant="outlined"
                            startIcon={<DesignServicesRoundedIcon />}
                            sx={{
                              textTransform: "none",
                              fontWeight: 900,
                              borderRadius: 2.5,
                            }}
                          >
                            Cargar servicio
                          </Button>
                        </Stack>
                      ) : canCreateItems ? (
                        <Button
                          onClick={handleOpenCreate}
                          variant="contained"
                          startIcon={<AddRoundedIcon />}
                          sx={{
                            mt: 1,
                            bgcolor: BRAND.orange,
                            textTransform: "none",
                            fontWeight: 900,
                            borderRadius: 2.5,
                          }}
                        >
                          Crear publicación
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : null}

              {paginatedProducts.map((product) => {
                const itemKind = normalizeItemKind(product);
                const isService = itemKind === "service";
                const capabilities = productCapabilities(product);
                const isActive = productIsActive(product);
                const imageUrl = getProductImageUrls(product)[0];
                const stock = productStock(product);
                const displayName = productName(product);
                const code = productCode(product);
                const unit = product.unit || product.unidad || "";
                const recipeSummary = recipeSummaryByProduct.get(String(productId(product))) || null;

                return (
                  <TableRow
                    key={productId(product)}
                    hover
                    sx={{
                      transition: "background-color .16s ease, box-shadow .16s ease",
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": {
                        bgcolor: "rgba(243,90,58,0.055)",
                        boxShadow: "inset 4px 0 0 rgba(243,90,58,.9)",
                      },
                      "&:hover .wp-items-actions-cell": { bgcolor: "rgba(243,90,58,0.055)" },
                    }}
                  >
                    <TableCell sx={{ minWidth: 245, py: 0.55 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Avatar
                          variant="rounded"
                          src={imageUrl || undefined}
                          alt={displayName}
                          sx={{
                            width: 36,
                            height: 36,
                            flex: "0 0 auto",
                            borderRadius: 1.8,
                            bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.06)",
                            border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)",
                            color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
                          }}
                        >
                          {isService ? <DesignServicesRoundedIcon fontSize="small" /> : <ImageRoundedIcon fontSize="small" />}
                        </Avatar>
                        <Stack direction="row" spacing={0.65} alignItems="center" sx={{ minWidth: 0 }}>
                          <Typography
                            noWrap
                            sx={{
                              minWidth: 0,
                              maxWidth: 255,
                              color: BRAND.dark,
                              fontWeight: 950,
                              lineHeight: 1.15,
                            }}
                          >
                            {displayName}
                          </Typography>
                          {product.hasVariants || product.variants?.length ? (
                            <Chip
                              size="small"
                              label={`${(product.variants || product.variations || []).length} var.`}
                              sx={{ height: 20, flex: "0 0 auto", fontSize: 10.5, fontWeight: 900 }}
                            />
                          ) : null}
                        </Stack>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={ITEM_KIND_LABELS[itemKind] || itemKind}
                        variant="outlined"
                        sx={{
                          fontWeight: 800,
                          borderColor: capabilities.publicVisible
                            ? "rgba(25,118,210,0.28)"
                            : "rgba(123,91,181,0.3)",
                          color: capabilities.publicVisible ? "#1565C0" : "#6B4CA5",
                          bgcolor: capabilities.publicVisible
                            ? "rgba(25,118,210,0.04)"
                            : "rgba(123,91,181,0.05)",
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: code ? "monospace" : "inherit",
                          color: code ? BRAND.dark : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.38)",
                          fontWeight: code ? 750 : 500,
                        }}
                      >
                        {code || "Sin código"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ color: BRAND.dark, fontWeight: 750, maxWidth: 165 }}>
                        {productCategory(product)}{productSubcategory(product) ? ` · ${productSubcategory(product)}` : ""}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography noWrap sx={{ color: BRAND.dark, fontWeight: 950 }}>
                        {priceLabel(product, catalogCurrency)}{unit ? ` / ${unit}` : ""}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {isService ? (
                        <Typography variant="body2" noWrap sx={{ color: BRAND.dark, fontWeight: 850 }}>
                          {durationLabel(product.durationMinutes || product.duracionMinutos)} · {product.modality || product.modalidad || "Sin modalidad"}
                        </Typography>
                      ) : capabilities.stockTracked ? (
                        stock === 0 && canManageStock ? (
                          <Tooltip title="Cargar el stock que ya tenés. El ítem se abre seleccionado en Movimientos.">
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<AddRoundedIcon sx={{ fontSize: 17 }} />}
                              onClick={() => openStockMovement(product)}
                              sx={{
                                minHeight: 32,
                                px: 1.2,
                                borderRadius: 999,
                                textTransform: "none",
                                fontWeight: 1000,
                                whiteSpace: "nowrap",
                                color: "#17181C",
                                bgcolor: BRAND.orange,
                                "@keyframes stockZeroPulse": {
                                  "0%,100%": { boxShadow: "0 0 0 0 rgba(243,90,58,.34)", transform: "scale(1)" },
                                  "45%": { boxShadow: "0 0 0 8px rgba(243,90,58,0)", transform: "scale(1.035)" },
                                },
                                animation: "stockZeroPulse 1.55s ease-in-out infinite",
                                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                                "&:hover": { bgcolor: "#FF714F", transform: "translateY(-1px)" },
                              }}
                            >
                              0 · Cargar stock
                            </Button>
                          </Tooltip>
                        ) : stock <= Number(product.inventory?.reorderPoint ?? 5) ? (
                          <Stack direction="row" spacing={0.65} alignItems="center">
                            <Typography sx={{ color: "#C4452D", fontWeight: 950 }}>{stock}</Typography>
                            {canManageStock ? (
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => openStockMovement(product)}
                                sx={{ minWidth: 0, px: 0.65, minHeight: 28, textTransform: "none", fontWeight: 900, color: "#B23D28" }}
                              >
                                Reponer
                              </Button>
                            ) : (
                              <Chip size="small" label={stock === 0 ? "Sin stock" : "Reponer"} sx={{ height: 21, fontSize: 10.5, fontWeight: 850, color: "#B23D28", bgcolor: "rgba(243,90,58,.09)" }} />
                            )}
                          </Stack>
                        ) : (
                          <Typography sx={{ color: BRAND.dark, fontWeight: 950 }}>{stock}</Typography>
                        )
                      ) : (
                        <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
                          Sin control de stock
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={isActive ? "Activo" : "Oculto"}
                        sx={{
                          fontWeight: 850,
                          color: isActive ? "#237A45" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.58)",
                          bgcolor: isActive
                            ? "rgba(46,160,83,0.1)"
                            : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.07)",
                        }}
                      />
                    </TableCell>

                    <TableCell align="right" className="wp-items-actions-cell">
                      <Stack direction="row" spacing={0.75} justifyContent="flex-end" useFlexGap flexWrap="nowrap">
                        {canUpdateItems ? (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<EditRoundedIcon sx={{ fontSize: 17 }} />}
                            onClick={() => handleOpenEdit(product)}
                            aria-label={`Editar ${displayName}`}
                            sx={{
                              minHeight: { xs: 44, md: 32 },
                              px: { xs: 1.4, md: 1.15 },
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 1000,
                              whiteSpace: "nowrap",
                              color: "#17181C",
                              bgcolor: BRAND.orange,
                              boxShadow: "0 4px 12px rgba(243,90,58,.2)",
                              "&:hover": { bgcolor: "#FF714F", boxShadow: "0 6px 16px rgba(243,90,58,.28)" },
                            }}
                          >
                            Editar
                          </Button>
                        ) : null}
                        {recipesEnabled && product.gastronomicMenu?.enabled === true ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RestaurantMenuRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={() => openRecipeForProduct(product)}
                            sx={{
                              minHeight: { xs: 44, md: 32 },
                              px: 1,
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 900,
                              whiteSpace: "nowrap",
                              color: recipeSummary ? "success.main" : BRAND.dark,
                            }}
                          >
                            Receta
                          </Button>
                        ) : null}
                        {canDeleteItems ? (
                          <Tooltip title="Eliminar ítem">
                            <IconButton
                              onClick={() => handleDelete(product)}
                              aria-label={`Eliminar ${displayName}`}
                              sx={{ minWidth: { xs: 44, md: 32 }, minHeight: { xs: 44, md: 32 }, color: BRAND.orange, border: { xs: "1px solid rgba(243,90,58,.24)", md: "none" }, borderRadius: 2 }}
                            >
                              <DeleteRoundedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </ResponsiveOperationalTable>
        </TableContainer>

        <Divider />
        <TablePagination
          component="div"
          count={filteredProducts.length}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[8, 16, 32]}
          labelRowsPerPage={compactLayout ? "Por página" : "Filas por página"}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          sx={{
            display: { xs: "none", md: "block" },
            color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.68)",
            px: { xs: 0.5, sm: 1.5 },
            ".MuiTablePagination-toolbar": { flexWrap: "nowrap", justifyContent: "flex-end", minHeight: 44 },
            ".MuiTablePagination-spacer": { display: { xs: "none", sm: "block" } },
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
              {
                fontWeight: 700,
              },
          }}
        />
        <MobileListPagination
          count={filteredProducts.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          label="ítems"
          sx={{ display: { xs: "block", md: "none" }, borderTop: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.08)" }}
        />
      </Paper>
        </>
      )}

      <ProductImportWizard
        open={importOpen}
        projectId={projectId}
        onImported={loadProducts}
        onClose={() => setImportOpen(false)}
      />

      <Dialog
        open={bulkPriceOpen}
        onClose={bulkPriceSaving ? undefined : () => setBulkPriceOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark }}>Ajuste masivo de precios</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              Se aplicará sobre <strong>{filteredProducts.length}</strong> ítem(s) del filtro actual.
              También se ajustan precios explícitos de variantes, escalas por cantidad y referencia de membresía.
            </Alert>
            <TextField
              autoFocus
              label="Porcentaje"
              type="number"
              value={bulkPricePercent}
              onChange={(event) => {
                setBulkPricePercent(event.target.value);
                setBulkPricePreview(null);
                setBulkPriceError("");
              }}
              inputProps={{ step: "0.01", min: "-99.99" }}
              helperText="Ej.: 12,5 para aumentar; -8 para reducir."
              fullWidth
            />
            {bulkPriceError ? <Alert severity="error">{bulkPriceError}</Alert> : null}
            {bulkPricePreview ? (
              <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.04)" }}>
                <Typography sx={{ fontWeight: 900, mb: 1 }}>
                  Vista previa · {bulkPricePreview.matched || 0} ítem(s)
                </Typography>
                <Stack spacing={.7}>
                  {(bulkPricePreview.sample || []).map((item) => (
                    <Stack key={item.id} direction="row" justifyContent="space-between" spacing={2}>
                      <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>{item.name}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900, whiteSpace: "nowrap" }}>
                        {Number(item.previousPrice || 0).toLocaleString("es-AR")} → {Number(item.newPrice || 0).toLocaleString("es-AR")}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setBulkPriceOpen(false)} disabled={bulkPriceSaving}>Cancelar</Button>
          <Button variant="outlined" onClick={previewBulkPriceAdjustment} disabled={bulkPriceSaving}>
            Vista previa
          </Button>
          <Button
            variant="contained"
            onClick={applyBulkPriceAdjustment}
            disabled={bulkPriceSaving || !bulkPricePreview?.matched}
            sx={{ bgcolor: BRAND.dark, "&:hover": { bgcolor: "#101820" } }}
          >
            Aplicar ajuste
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        fullScreen={mobile}
        maxWidth={guidedGastronomicForm ? "md" : "lg"}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            minHeight: mobile ? "100dvh" : "min(780px, calc(100dvh - 48px))",
            maxHeight: mobile ? "100dvh" : "calc(100dvh - 48px)",
            borderRadius: mobile ? 0 : 4,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            px: { xs: 2, sm: 3 },
            py: { xs: 1.8, sm: 2.3 },
            pr: 7,
            color: "#fff",
            background: "linear-gradient(135deg,#19242F 0%,#30495B 74%,#3B5A70 100%)",
            overflow: "hidden",
            "&::after": {
              content: '""',
              position: "absolute",
              width: 145,
              height: 145,
              right: -55,
              bottom: -95,
              borderRadius: "50%",
              bgcolor: "rgba(243,90,58,.27)",
            },
          }}
        >
          <Typography variant="overline" sx={{ fontWeight: 950, letterSpacing: ".12em", color: "#FFAA96" }}>
            {gastronomicMenuEnabled ? "CARTA E ÍTEMS" : "CATÁLOGO Y OPERACIÓN"}
          </Typography>
          <Typography variant={mobile ? "h6" : "h5"} sx={{ fontWeight: 950, lineHeight: 1.15 }}>
            {editingProduct
              ? guidedGastronomicForm
                ? `Editar ${form.erpItemKind === "supply" ? "ingrediente / insumo" : "plato / producto"}`
                : `Editar ${form.erpItemKind === "service" ? "servicio" : "ítem"}`
              : itemCreationChoiceOpen
                ? "¿Cómo querés crear este ítem?"
                : guidedGastronomicForm
                  ? `Nuevo ${form.erpItemKind === "supply" ? "ingrediente / insumo" : "plato / producto"}`
                  : `Nuevo ${form.erpItemKind === "service" ? "servicio" : "ítem"}`}
          </Typography>
          {!mobile ? (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,.72)", mt: 0.5 }}>
              {itemCreationChoiceOpen
                ? "Paso 0 · Elegí si querés entrar directo o repetir la guía campo por campo."
                : guidedGastronomicForm
                  ? "Completá primero lo esencial. La configuración ERP sigue disponible en Más opciones y las existencias se gestionan desde Movimientos."
                  : `Paso ${itemStep + 1} de ${ITEM_FORM_STEPS.length}`}
            </Typography>
          ) : null}
          <IconButton
            onClick={handleClose}
            disabled={saving || processingImages}
            aria-label="Cerrar formulario"
            sx={{ position: "absolute", right: 10, top: 10, color: "#fff", zIndex: 1 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        {/* WP-ITEM-FOCUS-COACH-TARGETS
            STATUS: IMPLEMENTED / STATIC_PASS / STAGING_PENDING
            Visual anchors only; the existing wizard remains the source of truth. */}
        <Box
          component="form"
          data-wp-telemetry-form={itemCreationChoiceOpen ? undefined : "item-editor"}
          data-wp-item-wizard={itemCreationChoiceOpen ? undefined : "true"}
          data-wp-item-step={itemCreationChoiceOpen ? undefined : itemStep}
          data-wp-item-kind={form.erpItemKind || "item"}
          data-wp-item-has-variants={form.hasVariants ? "true" : "false"}
          data-wp-item-image-count={existingImages.length + selectedFiles.length}
          onSubmit={handleSubmit}
          noValidate
          sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}
        >
          <DialogContent
            ref={formContentRef}
            dividers
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overscrollBehavior: "contain",
              p: mobile ? 1.25 : 2.1,
              bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Stack spacing={1.85}>
              {itemCreationChoiceOpen ? (
                <Stack spacing={2.2} sx={{ py: { xs: 1, sm: 2 }, maxWidth: 900, width: "100%", mx: "auto" }}>
                  {/* WP-ITEM-CREATE-STEP-ZERO
                      TEST: verify both paths on empty and established catalogs, desktop + mobile.
                      SAFETY: this screen does not mutate item data and never changes save payloads. */}
                  <Box sx={{ textAlign: "center", px: { xs: 0.5, sm: 2 } }}>
                    <Typography variant="overline" sx={{ color: BRAND.orange, fontWeight: 1000, letterSpacing: ".13em" }}>
                      PASO 0 · ELEGÍ TU FORMA DE CARGA
                    </Typography>
                    <Typography sx={{ mt: 0.5, fontSize: { xs: 25, sm: 32 }, lineHeight: 1.08, fontWeight: 1000, color: BRAND.dark }}>
                      Podés crear rápido o hacerlo acompañado
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, color: "text.secondary", maxWidth: 650, mx: "auto", lineHeight: 1.55 }}>
                      El resultado es el mismo. El tutorial se puede repetir siempre: no importa si ya cargaste otros ítems o si ya lo completaste antes.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2,minmax(0,1fr))" },
                      gap: 1.5,
                    }}
                  >
                    <ButtonBase
                      type="button"
                      onClick={chooseNormalItemCreation}
                      sx={{
                        borderRadius: 4,
                        textAlign: "left",
                        alignItems: "stretch",
                        overflow: "hidden",
                        border: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.14)",
                        bgcolor: "var(--wp-panel-surface,#fff)",
                        transition: "transform .16s ease, border-color .16s ease, box-shadow .16s ease",
                        "&:hover": { transform: "translateY(-2px)", borderColor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.32)", boxShadow: "0 16px 34px rgba(var(--wp-panel-shadow-rgb,25,36,47),.10)" },
                        "&.Mui-focusVisible": { outline: `3px solid ${BRAND.orange}`, outlineOffset: 3 },
                      }}
                    >
                      <Stack spacing={1.4} sx={{ p: { xs: 2.2, sm: 2.7 }, minHeight: { md: 245 }, width: "100%" }}>
                        <Box sx={{ width: 54, height: 54, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.06)", color: BRAND.dark }}>
                          <AddRoundedIcon sx={{ fontSize: 30 }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: { xs: 21, sm: 24 }, fontWeight: 1000, color: BRAND.dark }}>
                            Crear normalmente
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.65, color: "text.secondary", lineHeight: 1.55 }}>
                            Entrá directo al formulario y cargalo a tu ritmo. Ideal si ya sabés qué completa cada sección.
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: "auto !important", color: BRAND.dark }}>
                          <Typography sx={{ fontWeight: 950 }}>Ir al formulario</Typography>
                          <ArrowForwardRoundedIcon fontSize="small" />
                        </Stack>
                      </Stack>
                    </ButtonBase>

                    <ButtonBase
                      type="button"
                      onClick={chooseGuidedItemCreation}
                      sx={{
                        borderRadius: 4,
                        textAlign: "left",
                        alignItems: "stretch",
                        overflow: "hidden",
                        border: "1px solid rgba(243,90,58,.48)",
                        bgcolor: "rgba(243,90,58,.055)",
                        transition: "transform .16s ease, border-color .16s ease, box-shadow .16s ease",
                        "&:hover": { transform: "translateY(-2px)", borderColor: BRAND.orange, boxShadow: "0 18px 38px rgba(243,90,58,.18)" },
                        "&.Mui-focusVisible": { outline: `3px solid ${BRAND.orange}`, outlineOffset: 3 },
                      }}
                    >
                      <Stack spacing={1.4} sx={{ p: { xs: 2.2, sm: 2.7 }, minHeight: { md: 245 }, width: "100%" }}>
                        <Box sx={{ width: 54, height: 54, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.14)", color: BRAND.orange }}>
                          <AutoAwesomeRoundedIcon sx={{ fontSize: 30 }} />
                        </Box>
                        <Box>
                          <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
                            <Typography sx={{ fontSize: { xs: 21, sm: 24 }, fontWeight: 1000, color: BRAND.dark }}>
                              Usar tutorial
                            </Typography>
                            <Chip size="small" label="Se puede repetir" sx={{ height: 25, fontWeight: 950, bgcolor: "rgba(243,90,58,.13)", color: BRAND.dark }} />
                          </Stack>
                          <Typography variant="body2" sx={{ mt: 0.65, color: "text.secondary", lineHeight: 1.55 }}>
                            Te enfocamos cada campo, oscurecemos el resto y te explicamos nombre, descripción, categorías, precio, variantes, costos, publicación e imágenes.
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: "auto !important", color: BRAND.orange }}>
                          <Typography sx={{ fontWeight: 1000 }}>Empezar guía campo por campo</Typography>
                          <ArrowForwardRoundedIcon fontSize="small" />
                        </Stack>
                      </Stack>
                    </ButtonBase>
                  </Box>

                  <Typography variant="caption" sx={{ textAlign: "center", color: "text.secondary" }}>
                    Esta elección no cambia el ítem ni tus permisos. Sólo define cómo querés completar este alta.
                  </Typography>
                </Stack>
              ) : (
              <>
              {formFeedback ? (
                <Alert
                  severity={formFeedback.kind === "success" ? "success" : ["validation", "warning"].includes(formFeedback.kind) ? "warning" : "error"}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor:
                      formFeedback.kind === "success"
                        ? "rgba(46,125,50,.28)"
                        : ["validation", "warning"].includes(formFeedback.kind)
                          ? "rgba(237,108,2,.28)"
                          : "rgba(211,47,47,.28)",
                    alignItems: "flex-start",
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 950 }}>{formFeedback.title}</AlertTitle>
                  <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                    {formFeedback.message}
                  </Typography>
                  {formFeedback.detail ? (
                    <Typography variant="body2" sx={{ mt: 0.7, opacity: 0.82 }}>
                      {formFeedback.detail}
                    </Typography>
                  ) : null}
                  {formFeedback.requestId || (formFeedback.code && formFeedback.code !== "INTERNAL_ERROR") ? (
                    <Typography variant="caption" component="div" sx={{ mt: 0.85, fontWeight: 800, opacity: 0.72 }}>
                      {formFeedback.requestId ? `Solicitud: ${formFeedback.requestId}` : ""}
                      {formFeedback.requestId && formFeedback.code && formFeedback.code !== "INTERNAL_ERROR" ? " · " : ""}
                      {formFeedback.code && formFeedback.code !== "INTERNAL_ERROR" ? `Código: ${formFeedback.code}` : ""}
                    </Typography>
                  ) : null}
                </Alert>
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : null}

              {gastronomicMenuEnabled ? (
                <GastronomicItemWizard
                  form={form}
                  setForm={setForm}
                  fieldErrors={fieldErrors}
                  step={gastroStep}
                  setStep={setGastroStep}
                  editing={Boolean(editingProduct)}
                  menuView={menuView}
                  recipesView={recipesView}
                  currency={itemPriceCurrency}
                  sectionOptions={gastronomicSections}
                  subcategoryOptions={formSubcategories}
                  effectiveSection={effectiveGastronomicSection}
                  categoryLinked={gastroCategoryLinked}
                  onSectionChange={handleGastronomicSectionChange}
                  onCategoryChange={handleGastronomicCategoryChange}
                  onRoleChange={handleGastronomicRoleChange}
                  imageEditor={(
                    <ItemImagesEditor
                      existingImages={existingImages}
                      selectedFiles={selectedFiles}
                      previewUrls={previewUrls}
                      processingImages={processingImages}
                      saving={saving}
                      imagePreparationProgress={imagePreparationProgress}
                      imageUploadProgress={imageUploadProgress}
                      imageError={imageError}
                      maxImages={MAX_IMAGES}
                      mobile={mobile}
                      onRemoveExisting={handleRemoveExistingImage}
                      onRemoveSelected={handleRemoveSelectedFile}
                      onFilesChange={handleFilesChange}
                    />
                  )}
                  variantsEditor={(
                    <ProductVariantsEditor
                      form={form}
                      setForm={setForm}
                      imageOptions={variantImageOptions(existingImages, previewUrls)}
                      compact={compactLayout}
                      gastronomic
                      currency={itemPriceCurrency}
                    />
                  )}
                  positionEditor={(
                    <GastronomicSectionPositionEditor
                      products={products}
                      currentProductId={productId(editingProduct)}
                      currentName={form.name || "Este producto"}
                      sectionName={effectiveGastronomicSection}
                      sortOrder={form.gastronomicSortOrder}
                      onChange={(value) => setForm((current) => ({ ...current, gastronomicSortOrder: value }))}
                      onOpenFullOrder={() => setMenuOrderOpen(true)}
                    />
                  )}
                  quantityPricingEditor={(
                    <QuantityDiscountsEditor form={form} setForm={setForm} compact={compactLayout} gastronomic currency={itemPriceCurrency} />
                  )}
                  promotionsEditor={(
                    <GastronomicPromotionsEditor
                      drafts={promotionDrafts}
                      setDrafts={setPromotionDrafts}
                      products={products.filter((item) => normalizeItemKind(item) === "product")}
                      currentProductId={productId(editingProduct)}
                      currentProduct={editingProduct || { name: form.name, variants: form.variants }}
                      canManage={canUpdateItems}
                      loading={promotionLoading}
                      error={promotionError}
                      currency={itemPriceCurrency}
                    />
                  )}
                  currentStock={form.stock}
                  canManageStock={canManageStock}
                  stockMovementUrl={productId(editingProduct) ? `/dashboard/proyectos/${projectId}/sistema/movimientos?nuevo=1&tipo=stock_adjustment&itemId=${productId(editingProduct)}` : ""}
                  createdSupplyId={createdSupplyId}
                  advancedEditor={(
                    <GastronomicAdvancedEditor
                      form={form}
                      setForm={setForm}
                      categories={categories}
                      subcategories={formSubcategories}
                      onCategoryChange={handleGastronomicCategoryChange}
                      canManageCosts={canManageCosts}
                      canManageAccounting={canManageAccounting}
                      compact={compactLayout}
                      fieldErrors={fieldErrors}
                      editing={Boolean(editingProduct)}
                      onOpenScanner={() => setCreateScannerOpen(true)}
                      onDownloadCode={handleDownloadCurrentFormCode}
                      canDownloadCode={Boolean(String(form.code || "").trim() || (form.variants || []).some((variant) => variant?.barcode || variant?.sku))}
                    />
                  )}
                  hasAdvancedValues={Boolean(
                    String(form.code || "").trim()
                    || String(form.tags || "").trim()
                    || (form.attributes || []).length
                    || (form.erpItemKind === "product" && String(form.unit || "").trim())
                    || Number(form.lastCost || form.averageCost || form.standardCost || 0) > 0
                    || Number(form.minimumStock || form.reorderPoint || 0) > 0
                    || String(form.inventoryLocation || "").trim()
                    || String(form.taxProfileCode || "").trim()
                  )}
                />
              ) : null}

              <Box sx={{ display: guidedGastronomicForm ? "none" : "contents" }}>
              <ItemWizardNav
                step={itemStep}
                maxVisitedStep={itemMaxVisitedStep}
                onStepChange={goToItemStep}
              />

              <Box data-wp-item-step-section="0" sx={{ display: itemStep === 0 ? "contents" : "none" }}>
              <ItemFormHeading
                step="1"
                title="¿Qué vas a cargar?"
                description="Primero elegí qué representa este ítem. Después te mostramos sólo lo que importa para ese caso."
              />

              <Box>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark, mb: 0.35 }}>
                  Elegí el tipo de ítem
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.1 }}>
                  Producto está primero porque es el caso más común. Si no corresponde, tocá la opción que mejor describa lo que manejás.
                </Typography>
                <Box
                  role="radiogroup"
                  aria-label="Plantilla de ítem"
                  data-wp-item-coach-target="type"
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", md: "repeat(3,minmax(0,1fr))" },
                    gap: 1,
                  }}
                >
                  {Object.entries(ITEM_KIND_VISUALS).map(([value, meta]) => {
                    const selected = form.erpItemKind === value;
                    const KindIcon = meta.Icon;
                    return (
                      <ButtonBase
                        key={value}
                        role="radio"
                        aria-checked={selected}
                        onClick={() => handleGeneralItemKindChange(value)}
                        sx={{
                          minHeight: 86,
                          p: 1.15,
                          borderRadius: 2.6,
                          border: "1px solid",
                          borderColor: selected ? "rgba(243,90,58,.62)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
                          bgcolor: selected ? "rgba(243,90,58,.08)" : "var(--wp-panel-surface, #fff)",
                          boxShadow: selected ? "0 8px 22px rgba(243,90,58,.14)" : "none",
                          textAlign: "left",
                          justifyContent: "flex-start",
                          alignItems: "stretch",
                          transition: "transform .16s ease, box-shadow .16s ease, border-color .16s ease",
                          "&:hover": {
                            transform: "translateY(-1px)",
                            borderColor: "rgba(243,90,58,.45)",
                            boxShadow: "0 9px 22px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.10)",
                          },
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              flex: "0 0 auto",
                              borderRadius: 2,
                              display: "grid",
                              placeItems: "center",
                              bgcolor: selected ? BRAND.orange : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.055)",
                              color: selected ? "#fff" : BRAND.dark,
                            }}
                          >
                            <KindIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.15 }}>
                              {meta.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.25, display: "block", mt: 0.35 }}>
                              {meta.detail}
                            </Typography>
                          </Box>
                        </Stack>
                      </ButtonBase>
                    );
                  })}
                </Box>
              </Box>
              </Box>

              <Box data-wp-item-step-section="1" sx={{ display: itemStep === 1 ? "contents" : "none" }}>
              <ItemFormHeading
                step="2"
                title="Nombre y descripción"
                description="Dos cosas y listo: cómo se llama y una frase corta para reconocerlo y mostrarlo bien."
              />

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.35, sm: 1.65 },
                  borderRadius: 3,
                  borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.09)",
                  bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.016)",
                }}
              >
                <Stack spacing={1.35}>
                  <Box data-wp-item-coach-target="name">
                    <Typography variant="body2" sx={{ fontWeight: 950, color: BRAND.dark, mb: 0.55 }}>
                      {form.erpItemKind === "service"
                        ? "Elegí el nombre del servicio que estás por agregar"
                        : form.erpItemKind === "expense"
                          ? "Elegí el nombre del gasto que estás por agregar"
                          : "Elegí el nombre del ítem que estás por agregar"}
                    </Typography>
                    <TextField
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      error={Boolean(fieldErrors.name)}
                      placeholder={form.erpItemKind === "service" ? "Ej.: Instalación a domicilio" : "Ej.: Gazebo plegable 3x3"}
                      helperText={fieldErrors.name || "Usá el nombre con el que vos, tus empleados y tus clientes lo reconocerían."}
                      fullWidth
                      inputProps={{ "aria-label": "Nombre del ítem" }}
                    />
                  </Box>

                  <Box data-wp-item-coach-target="description">
                    <Typography variant="body2" sx={{ fontWeight: 950, color: BRAND.dark, mb: 0.55 }}>
                      Describilo en una frase
                    </Typography>
                    <TextField
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      multiline
                      minRows={2}
                      maxRows={3}
                      placeholder="Ej.: Gazebo plegable de estructura reforzada, fácil de transportar y armar."
                      helperText="¿No sabés qué escribir? Pedile a ChatGPT una descripción breve o tomá otra tienda como referencia y adaptala a tu producto."
                      fullWidth
                      inputProps={{ "aria-label": "Descripción breve del ítem" }}
                    />
                  </Box>
                </Stack>
              </Paper>
              </Box>

              <Box data-wp-item-step-section="2" sx={{ display: itemStep === 2 ? "contents" : "none" }}>
              <ItemFormHeading
                step="3"
                title="Categoría"
                description="Pensá dónde lo buscarías dentro de tu negocio. Acá sólo lo ordenamos; no cambia cómo funciona el producto."
              />
              <ItemOptionalStepNotice message="Si todavía no sabés dónde ubicarlo, podés omitir esta sección. WebProfit usa General y después lo podés reorganizar." />

              <Paper
                variant="outlined"
                data-wp-item-coach-target="classification"
                sx={{
                  p: { xs: 1.25, sm: 1.55 },
                  borderRadius: 3,
                  borderColor: "rgba(243,90,58,.20)",
                  bgcolor: "rgba(243,90,58,.028)",
                }}
              >
                <Stack spacing={1.2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        flex: "0 0 auto",
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "rgba(243,90,58,.12)",
                        color: BRAND.orange,
                      }}
                    >
                      <AccountTreeRoundedIcon sx={{ fontSize: 21 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                        Un juego simple: pasillo → estante
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Categoría = grupo grande. Subcategoría = una división dentro de ese grupo.
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(3,minmax(0,1fr))" },
                      gap: 0.75,
                    }}
                  >
                    {[
                      ["1", "Pensá dónde lo buscarías", "Ej.: Gazebos"],
                      ["2", "Elegí el grupo grande", "Categoría: Gazebos"],
                      ["3", "Dividilo sólo si ayuda", "Subcategoría: Plegables"],
                    ].map(([number, title, example]) => (
                      <Box
                        key={number}
                        sx={{
                          p: 0.9,
                          borderRadius: 2.2,
                          bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)",
                          border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)",
                          minWidth: 0,
                        }}
                      >
                        <Stack direction="row" spacing={0.65} alignItems="center">
                          <Box sx={{ width: 21, height: 21, borderRadius: "50%", bgcolor: number === "3" ? "rgba(243,90,58,.14)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", display: "grid", placeItems: "center", fontSize: 10, fontWeight: 950 }}>
                            {number}
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 900, color: BRAND.dark, lineHeight: 1.15 }}>
                            {title}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.45, pl: 3.35 }}>
                          {example}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "minmax(0,1fr)", md: "repeat(2,minmax(0,1fr))" },
                      gap: 1.05,
                    }}
                  >
                    <SearchPicker
                      freeSolo
                      options={categories}
                      value={form.category || null}
                      inputValue={form.category}
                      onChange={(_event, value) => {
                        const nextCategory = String(value || "");
                        setForm((current) => ({
                          ...current,
                          category: nextCategory,
                          subcategory: sameClassification(current.category, nextCategory)
                            ? current.subcategory
                            : "",
                        }));
                      }}
                      onInputChange={(_event, value, reason) => {
                        if (reason === "reset") return;
                        setForm((current) => ({
                          ...current,
                          category: value,
                          subcategory: sameClassification(current.category, value)
                            ? current.subcategory
                            : "",
                        }));
                      }}
                      label="Categoría"
                      placeholder="Ej.: Gazebos"
                      error={Boolean(fieldErrors.category)}
                      helperText={fieldErrors.category || "Grupo grande. Podés buscar uno existente o escribir uno nuevo."}
                      maxVisibleOptions={7}
                    />
                    <SearchPicker
                      freeSolo
                      options={formSubcategories}
                      value={form.subcategory || null}
                      inputValue={form.subcategory}
                      onChange={(_event, value) =>
                        setForm((current) => ({ ...current, subcategory: String(value || "") }))
                      }
                      onInputChange={(_event, value, reason) => {
                        if (reason === "reset") return;
                        setForm((current) => ({ ...current, subcategory: value }));
                      }}
                      label="Subcategoría"
                      placeholder="Ej.: Plegables"
                      helperText="Opcional. Usala sólo si realmente te ayuda a encontrarlo más rápido."
                      maxVisibleOptions={7}
                    />
                  </Box>

                  <Button
                    type="button"
                    size="small"
                    onClick={() => setForm((current) => ({ ...current, category: "General", subcategory: "" }))}
                    sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 900, px: 0.4 }}
                  >
                    No estoy seguro · usar General
                  </Button>
                </Stack>
              </Paper>
              </Box>

              <Box data-wp-item-step-section="3" sx={{ display: itemStep === 3 ? "contents" : "none" }}>
              <ItemFormHeading
                step="4"
                title="Precio de venta"
                description="Es el precio que va a aparecer en tu página web. No es costo ni precio de compra."
              />

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 1.3, sm: 1.6 },
                  borderRadius: 3,
                  borderColor: "rgba(243,90,58,.18)",
                  bgcolor: "rgba(243,90,58,.025)",
                }}
              >
                <Stack spacing={1.2}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "minmax(180px,.55fr) minmax(0,1fr)" },
                      gap: 1.2,
                      alignItems: "start",
                    }}
                  >
                    <Box data-wp-item-coach-target="price-type">
                      <Typography variant="caption" sx={{ display: "block", fontWeight: 950, color: BRAND.dark, mb: 0.5 }}>
                        Cómo se muestra
                      </Typography>
                      <TextField
                        select
                        name="priceType"
                        value={form.priceType}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        inputProps={{ "aria-label": "Forma de mostrar el precio" }}
                      >
                        <MenuItem value="fixed">Precio fijo</MenuItem>
                        <MenuItem value="from">Desde</MenuItem>
                        <MenuItem value="quote">Consultar</MenuItem>
                      </TextField>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ display: "block", fontWeight: 950, color: BRAND.dark, mb: 0.5 }}>
                        Moneda de venta
                      </Typography>
                      <TextField
                        select fullWidth size="small" name="priceCurrency"
                        value={itemPriceCurrency}
                        onChange={(event) => {
                          const nextCurrency = event.target.value;
                          setForm((current) => ({ ...current, priceCurrency: nextCurrency }));
                          setPriceInputDraft(formatCatalogMoneyValue(form.price || 0, nextCurrency));
                        }}
                        inputProps={{ "aria-label": "Moneda del precio de venta" }}
                      >
                        <MenuItem value="ARS">ARS · Pesos argentinos</MenuItem>
                        <MenuItem value="USD">USD · Dólares estadounidenses</MenuItem>
                        {!(["ARS", "USD"].includes(itemPriceCurrency)) ? <MenuItem value={itemPriceCurrency}>{itemPriceCurrency} · Moneda del proyecto</MenuItem> : null}
                      </TextField>
                    </Box>
                    {itemPriceCurrency === "USD" ? (
                      <Alert severity="warning" sx={{ gridColumn: "1 / -1" }}>
                        Este ítem se deshabilita para compras y pedidos del e-commerce porque todavía no procesamos ventas en dólares. Seguirá visible con su precio en USD y disponible para consultas por WhatsApp. No se convierte el importe automáticamente.
                      </Alert>
                    ) : null}

                    <Box data-wp-item-coach-target="price-value">
                      <Typography variant="caption" sx={{ display: "block", fontWeight: 950, color: BRAND.dark, mb: 0.5 }}>
                        {form.priceType === "quote" ? "Precio de referencia · opcional" : "Precio que verá el cliente"}
                      </Typography>
                      <TextField
                        name="price"
                        value={priceInputDraft}
                        onChange={(event) => {
                          const next = normalizeCatalogMoneyDraft(event.target.value, itemPriceCurrency);
                          setPriceInputDraft(next.display);
                          setForm((current) => ({ ...current, price: next.value }));
                          if (fieldErrors.price) {
                            setFieldErrors((current) => {
                              const nextErrors = { ...current };
                              delete nextErrors.price;
                              return nextErrors;
                            });
                          }
                        }}
                        onBlur={() => {
                          if (form.price === "") return;
                          setPriceInputDraft(formatCatalogMoneyValue(form.price, itemPriceCurrency, { fixedDecimals: true }));
                        }}
                        error={Boolean(fieldErrors.price)}
                        placeholder={itemPriceCurrency === "ARS" ? "12.500,00" : "1.550,00"}
                        fullWidth
                        size="small"
                        inputProps={{ inputMode: "decimal", "aria-label": "Precio de venta" }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{itemPriceCurrency === "ARS" ? "$" : itemPriceCurrency}</InputAdornment>,
                          endAdornment: itemPriceCurrency === "ARS" ? <InputAdornment position="end">ARS</InputAdornment> : null,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mt: 0.55, color: fieldErrors.price ? "error.main" : "text.secondary", lineHeight: 1.3 }}
                      >
                        {fieldErrors.price || (
                          itemPriceCurrency === "ARS"
                            ? "Escribí 12500 y WebProfit lo ordena como $ 12.500,00. También podés usar coma para centavos; no hace falta escribir puntos."
                            : `Precio de venta en ${itemPriceCurrency}. Sin conversión automática.`
                        )}
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: "1 / -1", p: { xs: 1.2, sm: 1.6 }, borderRadius: 2, bgcolor: "background.paper", border: "1px dashed", borderColor: "divider" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.3 }}>
                        Precio promocional tachado (página web)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.1, lineHeight: 1.5 }}>
                        Opcional. Indicá el precio anterior, por ejemplo 20.000. Se verá tachado junto al precio actual en las secciones, el catálogo y el detalle; no cambia lo que paga el cliente.
                      </Typography>
                      <TextField
                        fullWidth size="small" name="compareAtPrice" value={comparePriceInputDraft}
                        onChange={(event) => {
                          const parsed = normalizeCatalogMoneyDraft(event.target.value, itemPriceCurrency);
                          setComparePriceInputDraft(parsed.display);
                          setForm((current) => ({ ...current, compareAtPrice: parsed.value }));
                          if (fieldErrors.compareAtPrice) setFieldErrors((current) => ({ ...current, compareAtPrice: "" }));
                        }}
                        onBlur={() => setComparePriceInputDraft(formatCatalogMoneyValue(form.compareAtPrice, itemPriceCurrency, { fixedDecimals: form.compareAtPrice !== "" }))}
                        placeholder={itemPriceCurrency === "USD" ? "Ej.: 250,00" : "Ej.: 20.000"}
                        error={Boolean(fieldErrors.compareAtPrice)}
                        helperText={fieldErrors.compareAtPrice || "Dejalo vacío para no mostrar un precio tachado. Debe superar el precio de venta."}
                        inputProps={{ inputMode: "decimal", "aria-label": "Precio promocional tachado para la página web" }}
                        InputProps={{ startAdornment: <InputAdornment position="start">{itemPriceCurrency === "USD" ? "USD" : "$"}</InputAdornment> }}
                        sx={{ maxWidth: { sm: 350 } }}
                      />
                      {Number(form.compareAtPrice) > Number(form.price) && form.priceType !== "quote" ? (
                        <Stack direction="row" alignItems="baseline" flexWrap="wrap" gap={1} sx={{ mt: 1.1 }}>
                          <Typography variant="caption" color="text.secondary">Vista previa:</Typography>
                          <Typography variant="body2" sx={{ textDecoration: "line-through", color: "text.secondary" }}>{priceLabel({ price: form.compareAtPrice, priceCurrency: itemPriceCurrency }, itemPriceCurrency)}</Typography>
                          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{priceLabel({ price: form.price, priceCurrency: itemPriceCurrency }, itemPriceCurrency)}</Typography>
                        </Stack>
                      ) : null}
                    </Box>
                  </Box>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8} alignItems={{ sm: "center" }} justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      Después podés cambiar muchos precios juntos sin editar ítem por ítem.
                    </Typography>
                    <Button
                      type="button"
                      size="small"
                      variant="text"
                      startIcon={<TrendingUpRoundedIcon />}
                      onClick={openBulkPriceAdjustment}
                      sx={{ alignSelf: { xs: "flex-start", sm: "center" }, textTransform: "none", fontWeight: 900, px: 0.6 }}
                    >
                      ¿Querés modificar precios masivamente?
                    </Button>
                  </Stack>
                </Stack>
              </Paper>
              </Box>

              <Stack spacing={2.1}>
                <Box data-wp-item-step-section="4" sx={{ display: itemStep === 4 ? "contents" : "none" }}>

              {form.erpItemKind === "service" || supportsVariantsForKind(form.erpItemKind) ? (
                <>
                  <Divider />
                  <ItemFormHeading
                    step="5"
                    title={form.erpItemKind === "service" ? "Opciones del servicio" : "Opciones y variantes"}
                    description={form.erpItemKind === "service" ? "Turnos y modalidad, si aplica." : "Omitilo si existe una sola versión."}
                  />
                  <ItemOptionalStepNotice message={form.erpItemKind === "service" ? "Si no trabajás con turnos o reservas, podés omitir esta sección." : "Si este ítem tiene una sola versión y un solo precio, omití esta sección."} />

              {form.erpItemKind === "service" ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 3, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.02)" }}
                >
                  <Stack spacing={2}>
                    <Box>
                      <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                        Datos del servicio
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Duración, modalidad y turnos.</Typography>
                    </Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        label="Duración estimada (minutos)"
                        name="durationMinutes"
                        type="number"
                        value={form.durationMinutes}
                        onChange={handleChange}
                        error={Boolean(fieldErrors.durationMinutes)}
                        helperText={fieldErrors.durationMinutes || ""}
                        fullWidth
                        inputProps={{ min: 0 }}
                      />
                      <TextField
                        label="Modalidad"
                        name="modality"
                        value={form.modality}
                        onChange={handleChange}
                        placeholder="Presencial, online, a domicilio..."
                        fullWidth
                      />
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                          Requiere reserva o turno
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Publica la reserva online y, al guardar, te lleva a definir los horarios.
                        </Typography>
                      </Box>
                      <Switch
                        name="requiresBooking"
                        checked={isReservableService(form)}
                        onChange={(event) => {
                          const enabled = event.target.checked;
                          setForm((current) => ({
                            ...current,
                            reservable: enabled,
                            requiresBooking: enabled,
                            bookingProvider: enabled
                              ? (current.bookingProvider === "calendly" ? "calendly" : "webprofit")
                              : "none",
                            ...(!enabled
                              ? {
                                  calendlyUrl: "",
                                  calendlyDisplayMode: "popup",
                                  bookingConfirmationMode: "manual",
                                  bookingPaymentMode: "none",
                                  bookingDepositType: "fixed",
                                  bookingDepositValue: "0",
                                  capacity: "1",
                                  bufferBeforeMinutes: "0",
                                  bufferAfterMinutes: "0",
                                  meetingUrl: "",
                                  bookingInstructions: "",
                                  cancellationWindowHours: "0",
                                }
                              : {}),
                          }));
                        }}
                      />
                    </Stack>

                    {isReservableService(form) ? (
                      <>
                        <Divider />
                        <TextField
                          select
                          label="Agenda para la reserva"
                          name="bookingProvider"
                          value={form.bookingProvider === "calendly" ? "calendly" : "webprofit"}
                          onChange={handleChange}
                          helperText="Podés usar la agenda de WebProfit o delegar la elección del horario a Calendly."
                          fullWidth
                        >
                          <MenuItem value="webprofit">Agenda de WebProfit</MenuItem>
                          <MenuItem value="calendly">Calendly</MenuItem>
                        </TextField>

                        {form.bookingProvider === "calendly" ? (
                          <Stack spacing={2}>
                            <TextField
                              label="Enlace de Calendly"
                              name="calendlyUrl"
                              value={form.calendlyUrl}
                              onChange={handleChange}
                              placeholder="https://calendly.com/tu-negocio/turno"
                              error={Boolean(fieldErrors.calendlyUrl)}
                              helperText={fieldErrors.calendlyUrl || "Se abrirá este evento para que el cliente elija el horario."}
                              fullWidth
                            />
                            <TextField
                              select
                              label="Cómo mostrar Calendly"
                              name="calendlyDisplayMode"
                              value={form.calendlyDisplayMode}
                              onChange={handleChange}
                              fullWidth
                            >
                              <MenuItem value="popup">Ventana emergente</MenuItem>
                              <MenuItem value="inline">Integrado en la página</MenuItem>
                            </TextField>
                            <TextField
                              label="Instrucciones para reservar"
                              name="bookingInstructions"
                              value={form.bookingInstructions}
                              onChange={handleChange}
                              multiline
                              minRows={2}
                              placeholder="Indicaciones adicionales para el cliente..."
                              fullWidth
                            />
                          </Stack>
                        ) : (
                          <>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                          <TextField
                            select
                            label="Confirmación del turno"
                            name="bookingConfirmationMode"
                            value={form.bookingConfirmationMode}
                            onChange={handleChange}
                            fullWidth
                          >
                            <MenuItem value="manual">Confirmación manual</MenuItem>
                            <MenuItem value="automatic">Confirmación automática</MenuItem>
                          </TextField>
                          <TextField
                            select
                            label="Pago para reservar"
                            name="bookingPaymentMode"
                            value={form.bookingPaymentMode}
                            onChange={handleChange}
                            fullWidth
                          >
                            <MenuItem value="none">No requiere pago</MenuItem>
                            <MenuItem value="full">Pago total</MenuItem>
                            <MenuItem value="deposit">Seña</MenuItem>
                          </TextField>
                          <TextField
                            label="Cupos por horario"
                            name="capacity"
                            type="number"
                            value={form.capacity}
                            onChange={handleChange}
                            inputProps={{ min: 1, max: 10000 }}
                            error={Boolean(fieldErrors.capacity)}
                            helperText={fieldErrors.capacity || ""}
                            fullWidth
                          />
                        </Stack>

                        {form.bookingPaymentMode === "deposit" ? (
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                              select
                              label="Tipo de seña"
                              name="bookingDepositType"
                              value={form.bookingDepositType}
                              onChange={handleChange}
                              fullWidth
                            >
                              <MenuItem value="fixed">Monto fijo</MenuItem>
                              <MenuItem value="percentage">Porcentaje</MenuItem>
                            </TextField>
                            <TextField
                              label={
                                form.bookingDepositType === "percentage"
                                  ? "Porcentaje de seña"
                                  : "Monto de seña"
                              }
                              name="bookingDepositValue"
                              type="number"
                              value={form.bookingDepositValue}
                              onChange={handleChange}
                              inputProps={{
                                min: 0,
                                max:
                                  form.bookingDepositType === "percentage"
                                    ? 100
                                    : undefined,
                              }}
                              fullWidth
                            />
                          </Stack>
                        ) : null}

                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                          <TextField
                            label="Margen antes (minutos)"
                            name="bufferBeforeMinutes"
                            type="number"
                            value={form.bufferBeforeMinutes}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                          <TextField
                            label="Margen después (minutos)"
                            name="bufferAfterMinutes"
                            type="number"
                            value={form.bufferAfterMinutes}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                          <TextField
                            label="Cancelación mínima (horas)"
                            name="cancellationWindowHours"
                            type="number"
                            value={form.cancellationWindowHours}
                            onChange={handleChange}
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                        </Stack>

                        <TextField
                          label="Lugar o dirección"
                          name="serviceLocation"
                          value={form.serviceLocation}
                          onChange={handleChange}
                          placeholder="Consultorio, local, domicilio del cliente..."
                          fullWidth
                        />
                        <TextField
                          label="Enlace de videollamada"
                          name="meetingUrl"
                          value={form.meetingUrl}
                          onChange={handleChange}
                          placeholder="https://meet.google.com/..."
                          fullWidth
                        />
                        <TextField
                          label="Instrucciones para reservar"
                          name="bookingInstructions"
                          value={form.bookingInstructions}
                          onChange={handleChange}
                          multiline
                          minRows={2}
                          placeholder="Qué debe traer, cómo prepararse, condiciones del turno..."
                          fullWidth
                        />
                          </>
                        )}
                      </>
                    ) : null}
                  </Stack>
                </Paper>
              ) : null}

                  {supportsVariantsForKind(form.erpItemKind) ? (
                    <ProductVariantsEditor
                      form={form}
                      setForm={setForm}
                      imageOptions={variantImageOptions(existingImages, previewUrls)}
                      compact={compactLayout}
                      currency={itemPriceCurrency}
                    />
                  ) : null}
                </>
              ) : null}

                </Box>

                <Box data-wp-item-step-section="5" data-wp-item-coach-target="discounts" sx={{ display: itemStep === 5 ? "contents" : "none" }}>
              <Divider />
              <ItemFormHeading
                step="6"
                title="Descuentos por cantidad"
                description="Si vender más unidades cambia el precio, definilo acá. Si no, saltealo sin tocar nada."
              />
              <ItemOptionalStepNotice message="Esta sección es completamente opcional. Dejala vacía si vendés siempre al precio principal." />

              <QuantityDiscountsEditor form={form} setForm={setForm} compact={compactLayout} currency={itemPriceCurrency} />
                </Box>

                <Box data-wp-item-step-section="6" sx={{ display: itemStep === 6 ? "contents" : "none" }}>
              <Divider />
              <ItemFormHeading
                step="7"
                title="Uso"
                description="Elegí dónde participa este ítem. Cada tarjeta te explica qué cambia cuando la activás o la desactivás."
              />
              <ItemOptionalStepNotice message="Los valores recomendados ya vienen preparados según el tipo de ítem. Podés omitir esta sección si no necesitás cambiar nada." />

              <ItemOperationalFields
                form={form}
                setForm={setForm}
                canManageCosts={canManageCosts}
                canManageAccounting={canManageAccounting}
                membershipsEnabled={membershipsEnabled}
                compact={compactLayout}
                section="operations"
              />
                </Box>

                <Box data-wp-item-step-section="7" data-wp-item-coach-target="billing" sx={{ display: itemStep === 7 ? "contents" : "none" }}>
              <Divider />
              <ItemFormHeading
                step="8"
                title="Facturación"
                description="Impuestos y cuentas del ítem viven acá, separados de la venta y del stock."
              />
              <ItemOptionalStepNotice message="Podés omitir esta sección. Si todavía no configuraste facturación en el proyecto, el ítem se crea igual y podrás completarlo después." />

              <ItemOperationalFields
                form={form}
                setForm={setForm}
                canManageCosts={false}
                canManageAccounting={canManageAccounting}
                membershipsEnabled={false}
                compact={compactLayout}
                section="billing"
              />
                </Box>

                <Box data-wp-item-step-section="8" sx={{ display: itemStep === 8 ? "contents" : "none" }}>
              <Divider />
              <ItemFormHeading
                step="9"
                title="Publicar"
                description="Imágenes, etiquetas y datos opcionales para dejar el ítem más completo."
              />
              <ItemOptionalStepNotice message="Nada de esta sección es necesario para crear el ítem. Podés publicarlo así y completarlo cuando tengas tiempo." />

              <Box data-wp-item-coach-target="tags">
              <TextField
                label="Etiquetas"
                name="tags"
                value={form.tags}
                onChange={handleChange}
                placeholder="Ej.: premium, instalación, exterior"
                helperText="Opcional · separadas por comas"
                fullWidth
              />
              </Box>

              <Paper
                variant="outlined"
                data-wp-item-coach-target="technical"
                sx={{ p: { xs: 1.2, sm: 1.45 }, borderRadius: 3, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.014)" }}
              >
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>Código, unidad y datos técnicos</Typography>
                      <Typography variant="caption" color="text.secondary">Sólo si los necesitás. Se mantienen fuera del camino principal.</Typography>
                    </Box>
                    <Chip size="small" label="Opcional" sx={{ fontWeight: 900 }} />
                  </Stack>
              {advancedFormOpen ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "minmax(0,1fr)", md: "repeat(2,minmax(0,1fr))" },
                    gap: 2,
                  }}
                >
                  <Stack spacing={0.75}>
                    <TextField
                      label="Código interno / SKU"
                      name="code"
                      value={form.code}
                      onChange={(event) => {
                        handleChange(event);
                        setFormCodeNotice("");
                      }}
                      helperText={editingProduct ? "Se conserva si no lo cambiás." : "Opcional. Si queda vacío, WebProfit genera uno."}
                      fullWidth
                    />
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75}>
                      {!editingProduct ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<QrCodeScannerRoundedIcon />}
                          onClick={() => setCreateScannerOpen(true)}
                          sx={{ textTransform: "none", fontWeight: 900, justifyContent: "flex-start" }}
                        >
                          Escanear etiqueta
                        </Button>
                      ) : null}
                      {String(form.code || "").trim() || (form.variants || []).some((variant) => variant?.barcode || variant?.sku) ? (
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<DownloadRoundedIcon />}
                          onClick={handleDownloadCurrentFormCode}
                          sx={{ textTransform: "none", fontWeight: 900, justifyContent: "flex-start" }}
                        >
                          Descargar código
                        </Button>
                      ) : null}
                    </Stack>
                    {formCodeNotice ? (
                      <Typography variant="caption" sx={{ color: "success.main", fontWeight: 800 }}>
                        {formCodeNotice}
                      </Typography>
                    ) : null}
                  </Stack>
                  <TextField
                    label="Unidad"
                    name="unit"
                    value={form.unit}
                    onChange={handleChange}
                    placeholder={
                      form.erpItemKind === "service"
                        ? "sesión, hora, visita"
                        : "unidad, kg, m², mes"
                    }
                    fullWidth
                  />
                </Box>
              ) : null}

              <Button
                type="button"
                size="small"
                onClick={() => setAdvancedFormOpen((current) => !current)}
                sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 850, px: 0.5 }}
              >
                {advancedFormOpen ? "Ocultar datos opcionales" : "Más datos"}
              </Button>

              {advancedFormOpen && gastronomicMenuEnabled ? (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 3, bgcolor: "rgba(243,90,58,.025)" }}
                >
                  <Stack spacing={1.7}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      alignItems={{ sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>
                          Carta gastronómica
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Usa este mismo ítem, con su precio, imágenes, variantes y datos generales. Acá solo definís su presentación y disponibilidad dentro de la carta.
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 850 }}>
                          Incluir en carta
                        </Typography>
                        <Switch
                          name="gastronomicEnabled"
                          checked={form.gastronomicEnabled}
                          onChange={handleChange}
                        />
                      </Stack>
                    </Stack>

                    {form.gastronomicEnabled ? (
                      <>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1.5}
                          alignItems={{ sm: "center" }}
                          justifyContent="space-between"
                          sx={{ p: 1.5, borderRadius: 2.5, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.035)" }}
                        >
                          <Box>
                            <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                              Disponible ahora en la carta
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Podés pausarlo temporalmente sin desactivar el ítem, cambiar stock ni sacarlo del catálogo general.
                            </Typography>
                          </Box>
                          <Switch
                            name="gastronomicAvailable"
                            checked={form.gastronomicAvailable}
                            onChange={handleChange}
                          />
                        </Stack>
                        <TextField
                          label="Descripción corta para carta"
                          name="gastronomicShortDescription"
                          value={form.gastronomicShortDescription}
                          onChange={handleChange}
                          inputProps={{ maxLength: 280 }}
                          helperText={`${form.gastronomicShortDescription.length}/280 · Resumen breve para la carta; la descripción general del ítem se conserva aparte.`}
                          multiline
                          minRows={2}
                          fullWidth
                        />
                        <TextField
                          label="Observaciones alimentarias"
                          name="gastronomicDietaryNotes"
                          value={form.gastronomicDietaryNotes}
                          onChange={handleChange}
                          inputProps={{ maxLength: 500 }}
                          placeholder="Ej.: vegetariano, sin TACC, contiene frutos secos"
                          helperText="Solo información útil para elegir el plato. No reemplaza ingredientes, variantes ni atributos generales."
                          multiline
                          minRows={2}
                          fullWidth
                        />
                        <TextField
                          label="Sección de la carta"
                          name="gastronomicSection"
                          value={form.gastronomicSection}
                          onChange={handleChange}
                          placeholder={form.category || "Ej.: Entradas, Principales, Bebidas"}
                          helperText="Si queda vacío, la carta usa la categoría actual del ítem."
                          fullWidth
                        />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                          <TextField
                            label="Orden de sección"
                            name="gastronomicSectionOrder"
                            type="number"
                            value={form.gastronomicSectionOrder}
                            onChange={handleChange}
                            helperText="Menor número aparece primero."
                            fullWidth
                          />
                          <TextField
                            label="Orden dentro de la sección"
                            name="gastronomicSortOrder"
                            type="number"
                            value={form.gastronomicSortOrder}
                            onChange={handleChange}
                            helperText="No modifica el orden del catálogo."
                            fullWidth
                          />
                        </Stack>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography sx={{ fontWeight: 850, color: BRAND.dark }}>
                              Destacar en la carta
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Marca el ítem para presentaciones gastronómicas destacadas sin cambiar el catálogo.
                            </Typography>
                          </Box>
                          <Switch
                            name="gastronomicFeatured"
                            checked={form.gastronomicFeatured}
                            onChange={handleChange}
                          />
                        </Stack>
                      </>
                    ) : null}
                  </Stack>
                </Paper>
              ) : null}
                </Stack>
              </Paper>

              <Paper variant="outlined" data-wp-item-coach-target="additional" sx={{ p: 2, borderRadius: 3 }}>
                <Stack spacing={1.5}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    spacing={1}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                        Datos adicionales
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Marca, material, garantía, etc.</Typography>
                    </Box>
                    <Button
                      type="button"
                      startIcon={<AddCircleOutlineRoundedIcon />}
                      onClick={addAttribute}
                      sx={{ textTransform: "none", fontWeight: 850 }}
                    >
                      Agregar dato
                    </Button>
                  </Stack>

                  {form.attributes.length ? <Divider /> : null}
                  {form.attributes.map((row, index) => (
                    <Stack
                      key={`attribute-${index}`}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "center" }}
                    >
                      <TextField
                        label="Campo"
                        value={row.label}
                        onChange={(event) =>
                          handleAttributeChange(
                            index,
                            "label",
                            event.target.value,
                          )
                        }
                        placeholder="Ej.: Cobertura"
                        fullWidth
                      />
                      <TextField
                        label="Valor"
                        value={row.value}
                        onChange={(event) =>
                          handleAttributeChange(
                            index,
                            "value",
                            event.target.value,
                          )
                        }
                        placeholder="Ej.: Todo San Rafael"
                        fullWidth
                      />
                      <IconButton
                        type="button"
                        onClick={() => removeAttribute(index)}
                        aria-label="Eliminar dato"
                        sx={{ color: BRAND.orange }}
                      >
                        <DeleteRoundedIcon />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </Paper>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
               data-wp-item-coach-target="active">
                <Box>
                  <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                    Ítem activo
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Disponible para nuevas operaciones.</Typography>
                </Box>
                <Switch
                  name="active"
                  checked={form.active}
                  onChange={handleChange}
                />
              </Stack>

              <Box data-wp-item-coach-target="images">
                <Typography sx={{ fontWeight: 900, color: BRAND.dark, mb: 1 }}>
                  Imágenes
                </Typography>

                {existingImages.length > 0 ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ mb: 1.5 }}
                  >
                    {existingImages.map((img) => (
                      <Box
                        key={img.identifier}
                        sx={{ position: "relative", mb: 1 }}
                      >
                        <Box
                          component="img"
                          src={img.url}
                          alt="Imagen actual"
                          sx={{
                            width: 76,
                            height: 76,
                            objectFit: "cover",
                            borderRadius: 2,
                            border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.1)",
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleRemoveExistingImage(img.identifier)
                          }
                          aria-label="Quitar imagen actual"
                          sx={REMOVE_IMAGE_BUTTON_SX}
                        >
                          <CloseRoundedIcon sx={{ position: "absolute", top: 5, right: 5, fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                ) : null}

                {previewUrls.length > 0 ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ mb: 1.5 }}
                  >
                    {previewUrls.map((url, index) => (
                      <Box key={url} sx={{ position: "relative", mb: 1 }}>
                        <Box
                          component="img"
                          src={url}
                          alt={`Nueva imagen ${index + 1}`}
                          sx={{
                            width: 76,
                            height: 76,
                            objectFit: "cover",
                            borderRadius: 2,
                            border: `1px solid ${BRAND.orange}`,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            maxWidth: 76,
                            textAlign: "center",
                          }}
                        >
                          {formatImageSize(selectedFiles[index]?.size)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveSelectedFile(index)}
                          aria-label={`Quitar nueva imagen ${index + 1}`}
                          sx={REMOVE_IMAGE_BUTTON_SX}
                        >
                          <CloseRoundedIcon sx={{ position: "absolute", top: 5, right: 5, fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                ) : null}

                {processingImages ? (
                  <Box sx={{ mb: 1.5 }}>
                    <LinearProgress
                      variant={Number.isFinite(imagePreparationProgress) ? "determinate" : "indeterminate"}
                      value={Number.isFinite(imagePreparationProgress) ? imagePreparationProgress : undefined}
                      aria-label="Progreso de preparación de imágenes"
                    />
                    <Typography variant="caption" color="text.secondary">
                      Optimizando imágenes antes de subir
                      {Number.isFinite(imagePreparationProgress) ? `… ${imagePreparationProgress}%` : "…"}
                    </Typography>
                  </Box>
                ) : null}

                {saving && selectedFiles.length ? (
                  <Box sx={{ mb: 1.5 }}>
                    <LinearProgress
                      variant={Number.isFinite(imageUploadProgress) ? "determinate" : "indeterminate"}
                      value={Number.isFinite(imageUploadProgress) ? imageUploadProgress : undefined}
                      aria-label="Progreso de carga de imágenes"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {imageUploadProgress === 100
                        ? "Imágenes enviadas. El servidor está terminando de procesarlas…"
                        : `Subiendo imágenes${Number.isFinite(imageUploadProgress) ? `… ${imageUploadProgress}%` : "…"}`}
                    </Typography>
                  </Box>
                ) : null}

                {imageError ? (
                  <Alert severity="warning" sx={{ mb: 1.5 }}>
                    {imageError}
                  </Alert>
                ) : null}

                <Button
                  component="label"
                  disabled={
                    processingImages ||
                    existingImages.length + selectedFiles.length >= MAX_IMAGES
                  }
                  startIcon={<UploadRoundedIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 850,
                    color: BRAND.dark,
                    border: "1px dashed rgba(var(--wp-panel-ink-rgb, 25,36,47),0.3)",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                  }}
                >
                  {existingImages.length || selectedFiles.length
                    ? "Agregar más imágenes"
                    : "Subir imágenes"}
                  <input
                    type="file"
                    hidden
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFilesChange}
                  />
                </Button>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.7,
                    color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)",
                  }}
                >
                  {existingImages.length + selectedFiles.length}/{MAX_IMAGES} imágenes · JPG, PNG o WebP
                  {!mobile ? ` · hasta ${formatImageSize(PRODUCT_IMAGE_MAX_SOURCE_BYTES)} cada una. El panel las optimiza antes de enviarlas.` : ""}
                </Typography>
              </Box>
                </Box>

                <Box data-wp-item-step-section="9" sx={{ display: itemStep === 9 ? "contents" : "none" }}>
              <Divider />
              <ItemFormHeading
                step="10"
                title="Revisá y crealo"
                description="No hay nada nuevo para completar. Mirá lo esencial y guardá."
              />
              <Paper
                variant="outlined"
                data-wp-item-coach-target="review"
                sx={{
                  p: { xs: 1.4, sm: 1.8 },
                  borderRadius: 3,
                  borderColor: "rgba(243,90,58,.20)",
                  bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.018)",
                }}
              >
                <Stack spacing={1.2}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>
                        {ITEM_KIND_VISUALS[form.erpItemKind]?.label || "Ítem"}
                      </Typography>
                      <Typography sx={{ fontWeight: 1000, color: BRAND.dark, fontSize: { xs: 18, sm: 21 }, lineHeight: 1.15 }} noWrap>
                        {String(form.name || "Sin nombre").trim() || "Sin nombre"}
                      </Typography>
                    </Box>
                    <Chip
                      label={form.priceType === "quote" ? "Consultar" : `${itemPriceCurrency === "ARS" ? "$" : itemPriceCurrency} ${formatCatalogMoneyValue(form.price || 0, itemPriceCurrency, { fixedDecimals: itemPriceCurrency === "ARS" })}`}
                      sx={{ fontWeight: 1000, bgcolor: "rgba(243,90,58,.11)", color: BRAND.dark }}
                    />
                  </Stack>
                  <Divider />
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3,minmax(0,1fr))" }, gap: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Grupo</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>{form.category || "General"}{form.subcategory ? ` · ${form.subcategory}` : ""}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Opciones</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        {form.hasVariants ? `${(form.variants || []).length || 0} variantes` : "Una sola versión"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Estado</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>{form.active ? "Activo" : "Inactivo"}</Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Lo que omitiste queda con valores seguros y se puede editar después desde Catálogo.
                  </Typography>
                </Stack>
              </Paper>
                </Box>
              </Stack>
              </Box>
              </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{
              flex: "0 0 auto",
              p: mobile ? 1.2 : 2,
              gap: 1,
              bgcolor: "var(--wp-panel-surface, #fff)",
              borderTop: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
              boxShadow: "0 -10px 28px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.06)",
            }}
          >
            {itemCreationChoiceOpen ? (
              <Button
                type="button"
                onClick={handleClose}
                disabled={saving || processingImages}
                fullWidth={mobile}
                sx={{ textTransform: "none", fontWeight: 900 }}
              >
                Cancelar
              </Button>
            ) : (
            <>
            <Button
              onClick={handleClose}
              disabled={saving || processingImages}
              fullWidth={mobile}
              sx={{ textTransform: "none", fontWeight: 850 }}
            >
              Cancelar
            </Button>
            {guidedGastronomicForm && gastroStep > 0 ? (
              <Button
                type="button"
                onClick={() => setGastroStep((current) => Math.max(0, current - 1))}
                disabled={saving || processingImages}
                fullWidth={mobile}
                sx={{ textTransform: "none", fontWeight: 850 }}
              >
                Anterior
              </Button>
            ) : !guidedGastronomicForm && itemStep > 0 ? (
              <Button
                type="button"
                onClick={() => goToItemStep(itemStep - 1)}
                disabled={saving || processingImages}
                fullWidth={mobile}
                sx={{ textTransform: "none", fontWeight: 850 }}
              >
                Anterior
              </Button>
            ) : null}
            {!guidedGastronomicForm && itemStepOptional && itemStep < itemLastStep ? (
              <Button
                data-wp-item-coach-nav="skip"
                type="button"
                onClick={handleItemSkip}
                disabled={saving || processingImages}
                fullWidth={mobile}
                variant="outlined"
                endIcon={<ArrowForwardRoundedIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 950,
                  color: BRAND.dark,
                  borderColor: "rgba(243,90,58,.42)",
                  bgcolor: "rgba(243,90,58,.045)",
                  borderRadius: 999,
                  minHeight: 44,
                  minWidth: { sm: 260 },
                  px: 2,
                  "&:hover": { borderColor: BRAND.orange, bgcolor: "rgba(243,90,58,.09)" },
                }}
              >
                Omitir esta sección · es opcional
              </Button>
            ) : null}
            <Button
              data-wp-item-coach-nav="next"
              type={createdSupplyId
                ? "button"
                : guidedGastronomicForm && gastroStep < gastronomicLastStep
                  ? "button"
                  : !guidedGastronomicForm && itemStep < itemLastStep
                    ? "button"
                    : "submit"}
              onClick={createdSupplyId
                ? handleClose
                : guidedGastronomicForm && gastroStep < gastronomicLastStep
                  ? () => {
                    setFormFeedback(null);
                    setGastroStep((current) => Math.min(gastronomicLastStep, current + 1));
                    formContentRef.current?.scrollTo?.({ top: 0, behavior: "smooth" });
                  }
                  : !guidedGastronomicForm && itemStep < itemLastStep
                    ? handleItemNext
                    : undefined}
              disabled={saving || processingImages}
              variant="contained"
              startIcon={saving || processingImages ? <WebProfitSpinner size={17} /> : undefined}
              fullWidth={mobile}
              sx={{
                bgcolor: BRAND.orange,
                borderRadius: 999,
                minHeight: 44,
                textTransform: "none",
                fontWeight: 950,
                "&:hover": { bgcolor: "#D9472B" },
              }}
            >
              {createdSupplyId
                ? "Listo"
                : guidedGastronomicForm && gastroStep < gastronomicLastStep
                  ? "Continuar"
                  : !guidedGastronomicForm && itemStep < itemLastStep
                    ? "Siguiente"
                    : saving && savingStage === "promotions"
                      ? "Guardando promociones…"
                      : saving && selectedFiles.length
                        ? imageUploadProgress === 100
                          ? "Procesando imágenes…"
                          : `Subiendo${Number.isFinite(imageUploadProgress) ? ` ${imageUploadProgress}%` : "…"}`
                        : saving
                          ? "Guardando..."
                          : editingProduct
                            ? "Guardar cambios"
                            : form.erpItemKind === "supply"
                              ? "Crear insumo"
                              : guidedGastronomicForm
                                ? "Crear producto"
                                : "Crear ítem"}
            </Button>
            </>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      <GastronomicMenuOrderDialog
        open={menuOrderOpen}
        onClose={() => { if (!menuOrderSaving) setMenuOrderOpen(false); }}
        products={products}
        canEdit={canUpdateItems}
        saving={menuOrderSaving}
        error={menuOrderError}
        onSave={handleSaveMenuOrder}
      />

      <BarcodeScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleScannedItemCode}
        title="Buscar ítem con lectora"
        helper="Escaneá un código interno, SKU o código de barras de variante. WebProfit buscará una coincidencia exacta."
      />
      <BarcodeScannerDialog
        open={createScannerOpen}
        onClose={() => setCreateScannerOpen(false)}
        onScan={handleScannedCreateCode}
        title="Usar etiqueta existente"
        helper="Escaneá el código que ya trae este artículo. WebProfit verificará que no esté asignado y lo usará como código del nuevo ítem. Esto no modifica stock."
        submitLabel="Usar código"
      />
    </Stack>
  );
}
